<template>
  <b-container fluid class="datatable-container" :style="containerStyle">
    <b-row class="datatable-nav-row">
      <b-navbar type="dark" variant="primary" toggleable="md" id="context-nav" style="width: 100%;">
        <b-navbar-toggle target="nav_dropdown_collapse"></b-navbar-toggle>
        <b-collapse is-nav id="nav_dropdown_collapse">
          <div class="context-nav-actions">
            <b-button @click="showCreateSpreadSheet">new spreadsheet</b-button>
            <b-button @click="showImportSpreadSheet">import spreadsheet</b-button>
            <b-button @click="showCreateSpreadSheetColumn">new column</b-button>
            <b-button @click="showCreateSpreadSheetColumnsWithAi">
              <font-awesome-icon icon="wand-magic-sparkles" />
              ai add columns
            </b-button>
            <b-button @click="showCopySpreadSheet">copy spreadsheet</b-button>
            <b-button class="context-nav-danger" @click="showDeleteSpreadSheet">delete spreadsheet</b-button>
          </div>
        </b-collapse>
      </b-navbar>
    </b-row>
    <b-row class="datatable-main-row">
      <b-col id="spreadsheet-list-col" cols="3" class="datatable-sidebar-col">
        <div class="sidebar">
          <div class="sidebar-section-label">Spreadsheets</div>
          <b-alert v-if="spreadsheetsLoadError" variant="danger" show class="mx-2 mt-2 mb-1" style="font-size:0.8rem;">{{ spreadsheetsLoadError }}</b-alert>
          <draggable
            v-model="spreadsheets"
            item-key="uniqueTitle"
            handle=".drag-handle"
            ghost-class="drag-ghost"
            @end="onSpreadsheetDragEnd"
          >
            <template #item="{ element: spreadsheet }">
              <div class="sidebar-group">
                <div
                  class="sidebar-item"
                  :class="{ active: uniqueTitle === spreadsheet.uniqueTitle }"
                >
                  <DragHandle
                    :reorder-id="spreadsheet.uniqueTitle"
                    @move="moveSpreadsheetByKey(spreadsheet, $event)"
                  />
                  <button
                    type="button"
                    class="sidebar-open"
                    :aria-current="uniqueTitle === spreadsheet.uniqueTitle ? 'true' : undefined"
                    @click="loadSpreadSheet(spreadsheet)"
                  >
                    <font-awesome-icon icon="table" class="sidebar-icon" />
                    <span class="sidebar-label">{{ (spreadsheet && spreadsheet.title) ? spreadsheet.title : 'untitled' }}</span>
                  </button>
                  <span
                    v-if="loginPagesFor(spreadsheet).length"
                    class="login-sheet-badge"
                    :title="loginNoteFor(spreadsheet)"
                  >
                    <font-awesome-icon icon="key" />
                    login
                  </span>
                </div>
                <div v-if="spreadsheet && uniqueTitle === spreadsheet.uniqueTitle" class="sidebar-children">
                  <draggable
                    :model-value="spreadsheet.columns"
                    @update:model-value="onColumnsUpdate($event, spreadsheet)"
                    item-key="uniqueTitle"
                    handle=".column-drag-handle"
                    ghost-class="drag-ghost"
                    @end="onColumnDragEnd(spreadsheet)"
                  >
                    <template #item="{ element: column }">
                      <div class="sidebar-child-item">
                        <DragHandle
                          handle-class="column-drag-handle"
                          :reorder-id="column.uniqueTitle"
                          @move="moveColumnByKey(spreadsheet, column, $event)"
                        />
                        <button type="button" class="sidebar-open" :title="(column && column.title) ? column.title : 'untitled'" @click="showEditSpreadSheetColumn(column)">
                          <font-awesome-icon icon="stream" class="sidebar-child-icon" />
                          <span>{{ (column && column.title) ? column.title : 'untitled' }}</span>
                        </button>
                        <span class="sidebar-child-type">{{ columnTypeLabel(column.type) }}</span>
                      </div>
                    </template>
                  </draggable>
                  <button
                    type="button"
                    v-for="rev in reverseRelationshipColumns"
                    :key="rev.uniqueTitle"
                    class="sidebar-child-item sidebar-child-item-reverse"
                    :title="`Defined on '${rev.sourceSpreadsheet.title || rev.sourceSpreadsheet.uniqueTitle}' as '${rev.sourceColumn.title || rev.sourceColumn.uniqueTitle}'. Click to open it.`"
                    @click="openReverseRelationshipSource(rev)"
                  >
                    <font-awesome-icon icon="arrow-left" class="sidebar-child-icon sidebar-child-icon-reverse" />
                    <span>{{ rev.label }}</span>
                    <span class="sidebar-child-type">{{ columnTypeLabel(rev.inverseRelationshipType) }}</span>
                  </button>
                </div>
              </div>
            </template>
          </draggable>
        </div>
      </b-col>
      <b-col cols="9" class="px-0 spreadsheet-content-col">
        <div class="content-header" v-if="title">
          <div class="content-heading">
            <span class="content-title">{{ title }}</span>
            <span v-if="showsSeparateId(title, uniqueTitle)" class="content-unique-title">{{ uniqueTitle }}</span>
            <span
              v-if="openLoginPages.length"
              class="login-sheet-badge"
              :title="openLoginNote"
            >
              <font-awesome-icon icon="key" />
              login
            </span>
          </div>
          <div class="content-header-actions">
            <b-button v-if="selectedRows.length > 0" class="delete-rows-btn" size="sm" variant="danger" @click="showDeleteRowsModal">delete {{ selectedRows.length }} row{{ selectedRows.length === 1 ? '' : 's' }}</b-button>
            <b-button class="history-toggle-btn" size="sm" variant="outline-secondary" @click="showSpreadSheetHistory">
              <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" style="vertical-align: -2px; margin-right: 4px;"><path d="M8.515 1.019A7 7 0 0 0 8 1V0a8 8 0 1 1-7.73 5.743L1.065 5.37A7 7 0 1 0 8.515 1.019z"/><path d="M8 4.625a.375.375 0 0 0-.75 0V8c0 .13.067.25.178.318l2.5 1.5a.375.375 0 1 0 .394-.636L8 7.71V4.625z"/><path d="M.146 4.854a.5.5 0 0 1 0-.708l2-2a.5.5 0 1 1 .708.708L1.707 4H3.5a.5.5 0 0 1 0 1H.5a.5.5 0 0 1-.354-.146z"/></svg>
              history
            </b-button>
            <b-button class="filter-toggle-btn" :class="{ 'filter-toggle-btn-active': showFilters }" size="sm" variant="outline-secondary" @click="showFilters = !showFilters">
              <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" style="vertical-align: -2px; margin-right: 4px;"><path d="M1.5 1.5A.5.5 0 0 1 2 1h12a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-.128.334L10 8.692V13.5a.5.5 0 0 1-.342.474l-3 1A.5.5 0 0 1 6 14.5V8.692L1.628 3.834A.5.5 0 0 1 1.5 3.5v-2z"/></svg>
              filters
              <b-badge v-if="activeFilterCount" pill variant="light" class="ms-1">{{ activeFilterCount }}</b-badge>
            </b-button>
            <b-button class="export-csv-btn" size="sm" variant="outline-secondary" :disabled="csvExporting" title="Download this spreadsheet as CSV. Applied filters are included." @click="exportSpreadsheetCsv">
              {{ csvExporting ? 'exporting csv…' : 'export csv' }}
            </b-button>
            <b-button class="import-rows-btn" size="sm" variant="outline-secondary" title="Map a CSV onto this spreadsheet. Preview inserts, updates, and failures before anything is written." @click="showImportRows">
              import rows
            </b-button>
            <b-button class="new-row-btn" size="sm" @click="showNewRowModal">+ new row</b-button>
            <b-button class="bulk-actions-btn" size="sm" variant="outline-secondary" @click="showBulkActionsModal">
              <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" style="vertical-align: -2px; margin-right: 4px;"><path d="M2.5 1A1.5 1.5 0 0 0 1 2.5v3A1.5 1.5 0 0 0 2.5 7h3A1.5 1.5 0 0 0 7 5.5v-3A1.5 1.5 0 0 0 5.5 1h-3zM2 2.5a.5.5 0 0 1 .5-.5h3a.5.5 0 0 1 .5.5v3a.5.5 0 0 1-.5.5h-3a.5.5 0 0 1-.5-.5v-3zm6.5-1A1.5 1.5 0 0 0 7 3v10a1.5 1.5 0 0 0 1.5 1.5h5A1.5 1.5 0 0 0 15 13V3a1.5 1.5 0 0 0-1.5-1.5h-5zM8 3a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 .5.5v10a.5.5 0 0 1-.5.5h-5A.5.5 0 0 1 8 13V3zm-6 6a1.5 1.5 0 0 0-1.5 1.5v3A1.5 1.5 0 0 0 2 15h3.5A1.5 1.5 0 0 0 7 13.5v-3A1.5 1.5 0 0 0 5.5 9H2zm-.5 1.5A.5.5 0 0 1 2 10h3.5a.5.5 0 0 1 .5.5v3a.5.5 0 0 1-.5.5H2a.5.5 0 0 1-.5-.5v-3z"/></svg>
              bulk actions
            </b-button>
            <b-button class="scheduled-tasks-btn" size="sm" variant="outline-secondary" @click="showScheduledTasksModal">
              <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" style="vertical-align: -2px; margin-right: 4px;"><path d="M8 3.5a.5.5 0 0 0-1 0V8a.5.5 0 0 0 .252.434l3.5 2a.5.5 0 0 0 .496-.868L8 7.71V3.5z"/><path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm7-8A7 7 0 1 1 1 8a7 7 0 0 1 14 0z"/></svg>
              scheduled tasks
            </b-button>
            <b-button class="workflow-hooks-btn" size="sm" variant="outline-secondary" @click="showWorkflowHooksModal">
              hooks
            </b-button>
          </div>
        </div>
        <runtime-filter-builder
          v-if="showFilters && spreadSheet"
          :field-groups="filterFieldGroups"
          :initial-filters="[]"
          initial-match-mode="all"
          @filter-changed="onFilterChanged"
        />
        <b-alert v-if="gridWriteError" variant="danger" show dismissible class="mt-2" @dismissed="gridWriteError = null">{{ gridWriteError }}</b-alert>
        <div v-if="activeJobs.length" class="job-progress-bar">
          <div v-for="job in activeJobs" :key="job.jobId" class="job-progress-item">
            <div class="job-progress-info">
              <span class="job-progress-label">{{ jobLabel(job) }}</span>
              <span class="job-progress-pct">{{ job.progress }}%</span>
            </div>
            <div class="job-progress-track" role="progressbar" :aria-valuenow="job.progress" aria-valuemin="0" aria-valuemax="100" :aria-label="jobLabel(job)">
              <div class="job-progress-fill" :style="{ width: job.progress + '%' }"></div>
            </div>
          </div>
        </div>
        <div class="spreadsheet-wrapper" ref="scrollContainer" @scroll="onTableScroll">
          <div class="spreadsheet-table-container">
            <div id="hot-preview">
              <table v-if="tableFields.length > 0" class="table table-hover spreadsheet-table">
                <thead class="spreadsheet-sticky-header">
                  <tr>
                    <th class="select-cell">
                      <input
                        type="checkbox"
                        class="row-select-checkbox"
                        aria-label="Select all rows"
                        :checked="allRowsSelected"
                        :indeterminate.prop="someRowsSelected && !allRowsSelected"
                        @change="toggleSelectAll"
                      />
                    </th>
                    <th class="history-cell"><span class="visually-hidden">History</span></th>
                    <th v-for="field in tableFields" :key="field.key">{{ field.label }}</th>
                  </tr>
                </thead>
                <tbody>
                  <!-- Top spacer row -->
                  <tr v-if="virtualStartIndex > 0" class="virtual-spacer" aria-hidden="true" :style="{ height: virtualStartIndex * virtualRowHeight + 'px' }">
                    <td></td>
                    <td></td>
                    <td v-for="field in tableFields" :key="'st-' + field.key"></td>
                  </tr>
                  <!-- Visible rows -->
                  <tr v-for="(item, vIdx) in virtualItems" :key="item.id" :class="{ 'table-row-odd': (virtualStartIndex + vIdx) % 2 === 0 }">
                    <td class="select-cell">
                      <input
                        type="checkbox"
                        class="row-select-checkbox"
                        :aria-label="`Select row ${item.id}`"
                        :checked="selectedRowSet.has(item.id)"
                        @change="toggleRowSelection(item.id)"
                      />
                    </td>
                    <td class="history-cell">
                      <button type="button" class="row-history-btn" :aria-label="`View history for row ${item.id}`" @click.stop="showRecordHistory(item.id)">
                        <svg width="11" height="11" viewBox="0 0 16 16" fill="currentColor"><path d="M8.515 1.019A7 7 0 0 0 8 1V0a8 8 0 1 1-7.73 5.743L1.065 5.37A7 7 0 1 0 8.515 1.019z"/><path d="M8 4.625a.375.375 0 0 0-.75 0V8c0 .13.067.25.178.318l2.5 1.5a.375.375 0 1 0 .394-.636L8 7.71V4.625z"/></svg>
                      </button>
                      <button
                        v-if="openLoginPages.length"
                        type="button"
                        class="row-history-btn"
                        :aria-label="`Reset authenticator for row ${item.id}`"
                        title="Reset authenticator"
                        @click.stop="resetAuthenticator(item.id)"
                      >
                        <svg width="11" height="11" viewBox="0 0 16 16" fill="currentColor"><path d="M3.5 11.5a3.5 3.5 0 1 1 4.1-4.86.5.5 0 0 0 .4.2H11v1.5a.5.5 0 0 0 .5.5H13V10a.5.5 0 0 0 .5.5h1.086a.5.5 0 0 1 .353.146l.147.147a.5.5 0 0 1 0 .707l-.147.146a.5.5 0 0 0 0 .708l.147.146a.5.5 0 0 1 0 .707l-.147.147a.5.5 0 0 0-.353.146H13.5a.5.5 0 0 1-.5-.5V12h-1a.5.5 0 0 1-.5-.5V10H9.2a.5.5 0 0 0-.4.2 3.5 3.5 0 0 1-5.3 1.3zM5 10.5a1 1 0 1 0 0-2 1 1 0 0 0 0 2z"/></svg>
                      </button>
                    </td>
                    <td v-for="field in tableFields" :key="field.key">
                      <!-- Active editor: full component only for the cell being edited -->
                      <template v-if="editingCellKey === item.id + '::' + field.key">
                        <relationship-editor
                          v-if="isRelationshipField(field.key)"
                          :value="getRelationshipValue(item, field.key)"
                          :item="item"
                          :field-key="field.key"
                          :field-label="field.label"
                          :column-meta="columnMetaMap[field.key] || emptyColumnMeta"
                          :spreadsheet-unique-title="uniqueTitle"
                          :application-unique-title="$route.params.application"
                          @relationship-changed="onRelationshipChanged"
                        />
                        <rich-text-cell
                          v-else-if="fieldMap[field.key] && fieldMap[field.key].type === 'richText'"
                          :value="item[field.key]"
                          :item="item"
                          :field-key="field.key"
                          :field-label="field.label"
                          :spreadsheet-unique-title="uniqueTitle"
                          @save="updateCell"
                          @cancel="editingCellKey = null"
                        />
                        <image-cell
                          v-else-if="fieldMap[field.key] && (fieldMap[field.key].type === 'image' || fieldMap[field.key].type === 'file')"
                          :value="item[field.key]"
                          :item="item"
                          :field-key="field.key"
                          :field-label="field.label"
                          :accept="fieldMap[field.key].type === 'file' ? '' : 'image/*'"
                          @save="updateCellAndClear"
                          @cancel="editingCellKey = null"
                        />
                        <div v-else-if="fieldMap[field.key] && fieldMap[field.key].type === 'signature'" class="p-2">
                          <signature-pad @input="onSignatureDrawn(item, field.key, $event)" />
                          <button type="button" class="btn btn-sm btn-secondary mt-2" @click="editingCellKey = null">Close</button>
                        </div>
                        <div v-else-if="fieldMap[field.key] && fieldMap[field.key].type === 'duration'" class="p-2">
                          <label class="d-block mb-2">Start <input class="form-control form-control-sm" type="datetime-local" :value="durationPart(item, field.key, 'start')" @change="onDurationPart(item, field.key, 'start', $event)" /></label>
                          <label class="d-block mb-2">End <input class="form-control form-control-sm" type="datetime-local" :value="durationPart(item, field.key, 'end')" @change="onDurationPart(item, field.key, 'end', $event)" /></label>
                          <button type="button" class="btn btn-sm btn-primary" @click="saveDurationCell(item, field.key)">Save</button>
                          <button type="button" class="btn btn-sm btn-secondary" @click="editingCellKey = null">Cancel</button>
                        </div>
                        <editable-cell
                          v-else
                          :value="item[field.key]"
                          :item="item"
                          :field-key="field.key"
                          :field-label="field.label"
                          :field-type="fieldMap[field.key] ? fieldMap[field.key].type : 'text'"
                          :choices="columnChoices(field.key)"
                          :selection-mode="columnSelectionMode(field.key)"
                          :time-zone="viewerTimeZone"
                          :date-only="!!(fieldMap[field.key] && fieldMap[field.key].dateOnly)"
                          :read-only="field.key === 'id'"
                          @save="updateCellAndClear"
                          @cancel="editingCellKey = null"
                        />
                      </template>
                      <!-- Lightweight display span for all other cells -->
                      <button
                        v-else
                        type="button"
                        class="cell-display"
                        :class="cellClasses(item, field)"
                        :style="cellFormatStyle(item, field)"
                        :aria-label="cellAccessibleName(item, field)"
                        @click="onCellClick(item, field)"
                        @keydown.enter.prevent="onCellClick(item, field)"
                      >
                        <img
                          v-if="!cellFormatHidden(item, field) && cellImageUrl(item, field)"
                          :src="cellImageUrl(item, field)"
                          class="cell-image-thumb"
                          :alt="cellImageAlt(item, field)"
                        />
                        <template v-else>{{ cellFormatHidden(item, field) ? '' : cellDisplayText(item, field) }}</template>
                      </button>
                    </td>
                  </tr>
                  <!-- Bottom spacer row -->
                  <tr v-if="virtualEndIndex < items.length" class="virtual-spacer" aria-hidden="true" :style="{ height: (items.length - virtualEndIndex) * virtualRowHeight + 'px' }">
                    <td></td>
                    <td></td>
                    <td v-for="field in tableFields" :key="'sb-' + field.key"></td>
                  </tr>
                </tbody>
              </table>
              <div v-else class="text-muted p-4">Select a spreadsheet to view data</div>
            </div>
          </div>
        </div>
      </b-col>
    </b-row>

    <b-modal
      v-model="createSpreadSheetModalVisible"
      title="create new spreadsheet"
      @ok="onCreateSpreadSheetModalOk"
      :ok-disabled="createSpreadSheetAiSubmitting"
      :ok-title="createSpreadSheetActiveTab === 1 ? (createSpreadSheetAiSubmitting ? 'generating...' : 'create with AI') : 'OK'"
    >
      <template #title>
        create new spreadsheet
        <modal-help topic="create-spreadsheet" />
      </template>
      <b-tabs v-model="createSpreadSheetActiveTab">
        <b-tab title="manual" active>
          <b-form>
            <b-form-group label="spreadsheet title" :invalid-feedback="newSpreadSheetTitleFeedback" :state="newSpreadSheetTitleState">
              <b-form-input v-model="newSpreadSheetTitle" :state="newSpreadSheetTitleState" placeholder="my new spreadsheet name" required />
            </b-form-group>
            <b-form-group label="unique title" :invalid-feedback="newSpreadSheetUniqueTitleFeedback" :state="newSpreadSheetUniqueTitleState">
              <b-form-input :value="newSpreadSheetUniqueTitle" @input="onSpreadSheetUniqueTitleInput" :state="newSpreadSheetUniqueTitleState" placeholder="my_new_spreadsheet_name" required />
            </b-form-group>
          </b-form>
        </b-tab>
        <b-tab title="with AI">
          <b-form>
            <b-form-group label="describe the spreadsheet you want">
              <b-form-textarea
                v-model="aiSpreadSheetPrompt"
                placeholder="e.g. a CRM contact list with name, email, phone, company, last contacted date and notes"
                rows="4"
                max-rows="10"
                :disabled="createSpreadSheetAiSubmitting"
              />
              <small class="text-muted">The AI will pick a title and useful columns based on your description.</small>
              <AiTokenUsage />
            </b-form-group>
            <b-alert v-if="aiSpreadSheetError" variant="danger" show dismissible @dismissed="aiSpreadSheetError = null">{{ aiSpreadSheetError }}</b-alert>
            <b-alert v-if="aiSpreadSheetSuccess" variant="success" show dismissible @dismissed="aiSpreadSheetSuccess = null">{{ aiSpreadSheetSuccess }}</b-alert>
            <div v-if="createSpreadSheetAiSubmitting" class="text-muted">
              Generating spreadsheet with AI... this can take up to a minute.
            </div>
          </b-form>
        </b-tab>
      </b-tabs>
    </b-modal>

    <b-modal v-model="copySpreadSheetModalVisible" title="copy spreadsheet" @ok="copySpreadSheet" :ok-disabled="copySpreadSheetSubmitting" ok-title="copy">
      <template #title>
        copy spreadsheet
        <modal-help topic="copy-spreadsheet" />
      </template>
      <b-container fluid>
        <b-form>
          <b-row>
            <b-form-group label="new spreadsheet title" :invalid-feedback="copySpreadSheetTitleFeedback" :state="copySpreadSheetTitleState">
              <b-form-input v-model="copySpreadSheetTitle" :state="copySpreadSheetTitleState" placeholder="my copied spreadsheet" required />
            </b-form-group>
          </b-row>
          <b-row>
            <b-form-group label="unique title" :invalid-feedback="copySpreadSheetUniqueTitleFeedback" :state="copySpreadSheetUniqueTitleState">
              <b-form-input :value="copySpreadSheetUniqueTitle" @input="onCopySpreadSheetUniqueTitleInput" :state="copySpreadSheetUniqueTitleState" placeholder="my_copied_spreadsheet" required />
            </b-form-group>
          </b-row>
          <b-row>
            <b-form-group>
              <b-form-checkbox v-model="copySpreadSheetWithRecords">
                copy records
              </b-form-checkbox>
              <small class="text-muted d-block mt-1">When enabled, all rows will be copied. Relationship values for relationship columns on this spreadsheet will also be preserved.</small>
            </b-form-group>
          </b-row>
          <b-alert v-if="copySpreadSheetError" variant="danger" show dismissible @dismissed="copySpreadSheetError = null">{{ copySpreadSheetError }}</b-alert>
        </b-form>
      </b-container>
    </b-modal>

    <b-modal v-model="deleteSpreadSheetModalVisible" title="delete spreadsheet" @ok="deleteSpreadSheet" ok-variant="danger" ok-title="delete">
      <template #title>
        delete spreadsheet
        <modal-help topic="delete-spreadsheet" />
      </template>
      <b-container fluid>
        <b-form>
          <b-row>
            <b-form-group>
              delete spreadsheet {{title}}?
            </b-form-group>
          </b-row>
          <b-row>
            <b-form-group label="enter the spreadsheet title to confirm deletion">
              <b-form-input v-model="deleteSpreadSheetTitle" placeholder="my spreadsheet name" required />
            </b-form-group>
          </b-row>
        </b-form>
      </b-container>
    </b-modal>

    <b-modal v-model="createSpreadSheetColumnModalVisible" title="create new column" @ok="createSpreadSheetColumn" :ok-disabled="newColumnUniqueTitleCollision">
      <template #title>
        create new column
        <modal-help topic="create-column" />
      </template>
      <b-container fluid :key="createColumnFormKey">
        <b-alert v-if="newColumnServerError" variant="danger" show>{{ newColumnServerError }}</b-alert>
        <b-form>
          <b-row>
            <b-form-group label="column title" :invalid-feedback="newColumnTitleFeedback" :state="newColumnTitleState">
              <b-form-input v-model="newSpreadSheetColumnTitle" :state="newColumnTitleState" placeholder="my new spreadsheet column name" required />
            </b-form-group>
          </b-row>
          <b-row>
            <b-form-group label="unique title" :invalid-feedback="newColumnUniqueTitleFeedback" :state="newColumnUniqueTitleState">
              <b-form-input :value="newSpreadSheetUniqueColumnTitle" @input="onUniqueColumnTitleInput" :state="newColumnUniqueTitleState" placeholder="my_new_spreadsheet_column_name" required />
              <small v-if="newSpreadSheetUniqueColumnType === 'relationship' && !newColumnUniqueTitleFeedback" class="text-muted">Relationship names have to be unique in the application.</small>
            </b-form-group>
          </b-row>
          <b-row>
            <b-form-group label="column type" :invalid-feedback="newColumnTypeFeedback" :state="newColumnTypeState">
              <b-form-select v-model="newSpreadSheetUniqueColumnType" :options="columnTypes" :state="newColumnTypeState" class="mb-3" size="sm" placeholder="select column type" required />
            </b-form-group>
          </b-row>
          <b-row v-if="newSpreadSheetUniqueColumnType === 'dateTime'">
            <b-form-group label="date only">
              <b-form-checkbox v-model="newSpreadSheetColumnDateOnly">Store a calendar date, with no time</b-form-checkbox>
              <small class="text-muted">The day stays the same for every viewer. A date and time is stored in UTC and shown in the viewer's time zone.</small>
            </b-form-group>
          </b-row>
          <b-row v-if="newSpreadSheetUniqueColumnType === 'image'">
            <b-form-group label="auto-resize">
              <b-form-checkbox v-model="newSpreadSheetColumnAutoResize">Resize on upload</b-form-checkbox>
              <div v-if="newSpreadSheetColumnAutoResize" class="mt-2">
                <b-row>
                  <b-col cols="6">
                    <label class="form-label" style="font-size: 0.85rem;">{{ newSpreadSheetColumnKeepProportions ? 'max width (px)' : 'width (px)' }}</label>
                    <b-form-input v-model.number="newSpreadSheetColumnResizeWidth" type="number" min="1" size="sm" />
                  </b-col>
                  <b-col cols="6">
                    <label class="form-label" style="font-size: 0.85rem;">{{ newSpreadSheetColumnKeepProportions ? 'max height (px)' : 'height (px)' }}</label>
                    <b-form-input v-model.number="newSpreadSheetColumnResizeHeight" type="number" min="1" size="sm" />
                  </b-col>
                </b-row>
                <b-form-checkbox v-model="newSpreadSheetColumnKeepProportions" class="mt-2">Keep proportions</b-form-checkbox>
                <small class="text-muted d-block mt-1">{{ newSpreadSheetColumnKeepProportions ? 'The image is scaled so both sides stay within this width and height. Its shape stays the same.' : 'The image is stretched to this exact width and height.' }} A form can also take an image address. The server downloads that file and stores it.</small>
              </div>
            </b-form-group>
          </b-row>
          <b-row v-if="newSpreadSheetUniqueColumnType === 'duration'">
            <b-form-group label="unit">
              <b-form-select v-model="newSpreadSheetColumnDurationUnit" :options="durationUnitOptions" class="mb-3" size="sm" />
              <small class="text-muted">The form asks for a start and an end. The column stores the time between them in this unit.</small>
            </b-form-group>
          </b-row>
          <b-row v-if="newSpreadSheetUniqueColumnType === 'sequence'">
            <b-form-group label="start at">
              <b-form-input v-model.number="newSpreadSheetColumnSequenceStart" type="number" step="1" size="sm" />
              <small class="text-muted">The first record gets this number. The next record gets the number after the highest one already stored. It is not shown on a form and cannot be edited.</small>
            </b-form-group>
          </b-row>
          <b-row v-if="newSpreadSheetUniqueColumnType === 'signature'">
            <small class="text-muted d-block mb-3">A person draws on a canvas. The drawing is stored as an image, along with who signed and when.</small>
          </b-row>
          <b-row v-if="newSpreadSheetUniqueColumnType === 'relationship'">
            <b-form-group label="related spreadsheet">
              <b-form-select v-model="newSpreadSheetUniqueColumnRelationedSpreadSheet" :options="spreadSheetOptions" class="mb-3" size="sm" placeholder="select related spreadsheet" required />
            </b-form-group>
          </b-row>
          <b-row v-if="newSpreadSheetUniqueColumnType === 'multiselect'">
            <b-form-group label="selection mode">
              <b-form-select v-model="newSpreadSheetColumnSelectionMode" :options="selectionModeOptions" class="mb-3" size="sm" />
              <small class="text-muted">This controls whether the form can save one value or many. The form chooses the input control.</small>
            </b-form-group>
          </b-row>
          <b-row v-if="newSpreadSheetUniqueColumnType === 'multiselect'">
            <b-form-group label="choices">
              <small class="text-muted">Selectable values for this field.</small>
              <div v-for="(choice, index) in newSpreadSheetColumnChoices" :key="index" class="choice-row">
                <b-form-input v-model="newSpreadSheetColumnChoices[index]" size="sm" placeholder="choice" />
                <span class="choice-remove" @click="newSpreadSheetColumnChoices.splice(index, 1)">x</span>
              </div>
              <div class="add-choice" @click="newSpreadSheetColumnChoices.push('')">+ add choice</div>
            </b-form-group>
          </b-row>
          <b-row v-if="newSpreadSheetUniqueColumnType === 'relationship'">
            <b-form-group label="relationship type">
              <b-form-select v-model="newSpreadSheetUniqueColumnRelationshipType" :options="newColumnRelationshipTypes" class="mb-3" size="sm" placeholder="select relationship type" required />
            </b-form-group>
          </b-row>
          <relationship-column-settings
            v-if="newSpreadSheetUniqueColumnType === 'relationship'"
            v-model="newRelationshipSettings"
            :related-columns="newRelatedSheetColumns"
            :owner-columns="(spreadSheet && spreadSheet.columns) || []"
            :related-spread-sheet="newSpreadSheetUniqueColumnRelationedSpreadSheet"
          />
        </b-form>
      </b-container>
    </b-modal>

    <b-modal v-model="createSpreadSheetColumnsWithAiModalVisible" title="add columns with AI" @ok="createSpreadSheetColumnsWithAi" :ok-disabled="aiColumnsSubmitting" :ok-title="aiColumnsSubmitting ? 'generating...' : 'create with AI'">
      <template #title>
        add columns with AI
        <modal-help topic="add-columns-ai" />
      </template>
      <b-container fluid>
        <b-form>
          <b-row>
            <b-form-group :label="aiColumnsSpreadSheetLabel">
              <b-form-textarea
                v-model="aiColumnsPrompt"
                placeholder="e.g. add a phone number column, an address column and a checkbox-style notes column"
                rows="4"
                max-rows="10"
                :disabled="aiColumnsSubmitting"
              />
              <small class="text-muted">The AI will choose appropriate names and types for the columns you describe.</small>
              <AiTokenUsage />
            </b-form-group>
          </b-row>
          <b-alert v-if="aiColumnsError" variant="danger" show dismissible @dismissed="aiColumnsError = null">{{ aiColumnsError }}</b-alert>
          <b-alert v-if="aiColumnsSuccess" variant="success" show dismissible @dismissed="aiColumnsSuccess = null">{{ aiColumnsSuccess }}</b-alert>
          <div v-if="aiColumnsSubmitting" class="text-muted">
            Generating columns with AI... this can take up to a minute.
          </div>
        </b-form>
      </b-container>
    </b-modal>

    <edit-column-modal
      :visible="editSpreadSheetColumnModalVisible"
      :column="editColumnData"
      :column-types="columnTypes"
      :spread-sheet-options="spreadSheetOptions"
      :current-spreadsheet-columns="currentSpreadsheetColumns"
      :all-spreadsheets="spreadsheets"
      :current-spreadsheet-unique-title="uniqueTitle"
      :server-error="editColumnServerError"
      @update:visible="editSpreadSheetColumnModalVisible = $event"
      @save="editSpreadSheetColumn"
      @dismiss-error="editColumnServerError = ''"
      @show-delete="showDeleteColumn"
    />

    <b-modal v-model="deleteColumnModalVisible" title="delete column" @ok="deleteColumn" ok-variant="danger" ok-title="delete">
      <template #title>
        delete column
        <modal-help topic="delete-column" />
      </template>
      <b-container fluid>
        <b-form>
          <b-row>
            <b-form-group>
              delete column {{editSpreadSheetUniqueColumnTitle}}?
            </b-form-group>
          </b-row>
          <b-row>
            <b-form-group label="enter the column title to confirm deletion">
              <b-form-input v-model="deleteColumnTitle" placeholder="my column name" required />
            </b-form-group>
          </b-row>
        </b-form>
      </b-container>
    </b-modal>

    <b-modal v-model="newRowModalVisible" title="add new row" @ok="createNewRowFromModal" :ok-disabled="newRowSubmitting">
      <template #title>
        add new row
        <modal-help topic="add-row" />
      </template>
      <b-container fluid>
        <b-alert v-if="newRowError" variant="danger" show>{{ newRowError }}</b-alert>
        <b-form>
          <b-row v-for="col in editableNewRowColumns" :key="col.uniqueTitle">
            <b-form-group :label="newRowFieldLabel(col)" :description="col.description || ''">
              <b-form-input
                v-if="col.type === 'text' || col.type === 'password'"
                :model-value="newRowData[col.uniqueTitle]"
                @update:model-value="onNewRowFieldInput(col.uniqueTitle, $event)"
                :type="col.type === 'password' ? 'password' : 'text'"
                :placeholder="col.title"
              />
              <b-form-input
                v-else-if="col.type === 'number'"
                :model-value="newRowData[col.uniqueTitle]"
                @update:model-value="onNewRowFieldInput(col.uniqueTitle, $event)"
                type="number"
                :placeholder="col.title"
              />
              <b-form-input
                v-else-if="col.type === 'dateTime'"
                :model-value="newRowData[col.uniqueTitle]"
                @update:model-value="onNewRowFieldInput(col.uniqueTitle, $event)"
                :type="col.dateOnly ? 'date' : 'datetime-local'"
                :placeholder="col.title"
              />
              <div v-else-if="col.type === 'image' || col.type === 'file'" class="image-file-field">
                <input v-if="col.type === 'image'" class="form-control form-control-sm" type="file" accept="image/*" @change="onImageFieldFile('new', col.uniqueTitle, col.type, $event)" />
                <input v-else class="form-control form-control-sm" type="file" @change="onImageFieldFile('new', col.uniqueTitle, col.type, $event)" />
                <input v-if="col.type === 'image'" class="form-control form-control-sm mt-1" type="url" placeholder="https://example.com/photo.jpg" @change="onImageUrl('new', col.uniqueTitle, $event)" />
                <small v-if="imageUploadName(newRowData[col.uniqueTitle])" class="text-muted d-block mt-1">{{ imageUploadName(newRowData[col.uniqueTitle]) }}</small>
              </div>
              <div v-else-if="col.type === 'signature'">
                <signature-pad autosave @input="onNewRowFieldInput(col.uniqueTitle, $event)" />
              </div>
              <div v-else-if="col.type === 'duration'">
                <label class="d-block mb-2">Start <input class="form-control form-control-sm" type="datetime-local" @change="onNewDurationPart(col.uniqueTitle, 'start', $event)" /></label>
                <label class="d-block">End <input class="form-control form-control-sm" type="datetime-local" @change="onNewDurationPart(col.uniqueTitle, 'end', $event)" /></label>
                <small v-if="newDurationSummary(col)" class="text-muted">{{ newDurationSummary(col) }}</small>
              </div>
              <multiselect-choices
                v-else-if="col.type === 'multiselect' && hasColumnChoices(col)"
                :model-value="newRowData[col.uniqueTitle]"
                :choices="col.choices"
                :selection-mode="col.selectionMode || 'multiple'"
                :label="col.title"
                :radio-name="'new-row-' + col.uniqueTitle"
                @update:model-value="onNewRowFieldInput(col.uniqueTitle, $event)"
              />
              <div v-else-if="col.type === 'relationship'" class="new-row-rel-picker">
                <div v-if="(newRowData[col.uniqueTitle] || []).length > 0" class="new-row-rel-tags mb-2">
                  <span
                    v-for="rec in newRowData[col.uniqueTitle]"
                    :key="rec.id"
                    class="linked-record-tag"
                  >
                    <span class="linked-record-label">{{ rec.displayValue }}</span>
                    <span class="linked-record-remove" @click="removeNewRowRelSelection(col.uniqueTitle, rec.id)">&times;</span>
                  </span>
                </div>
                <b-form-input
                  :model-value="(newRowRelState[col.uniqueTitle] || {}).query || ''"
                  @update:model-value="onNewRowRelSearch(col.uniqueTitle, col, $event)"
                  size="sm"
                  placeholder="Search to link..."
                  class="mb-1"
                />
                <div v-if="((newRowRelState[col.uniqueTitle] || {}).results || []).length > 0" class="search-results">
                  <div
                    v-for="result in newRowRelState[col.uniqueTitle].results"
                    :key="result.id"
                    class="search-result-item"
                    @click="addNewRowRelSelection(col.uniqueTitle, col, result)"
                  >
                    <span class="search-result-label">{{ result.displayValue }}</span>
                    <span class="search-result-id">#{{ result.id }}</span>
                  </div>
                </div>
                <div v-else-if="(newRowRelState[col.uniqueTitle] || {}).query && !(newRowRelState[col.uniqueTitle] || {}).searching" class="text-muted" style="font-size: 0.8rem;">No results</div>
                <div v-if="(newRowRelState[col.uniqueTitle] || {}).searching" class="text-muted" style="font-size: 0.8rem;">Searching...</div>
              </div>
              <b-form-input
                v-else
                :model-value="newRowData[col.uniqueTitle]"
                @update:model-value="onNewRowFieldInput(col.uniqueTitle, $event)"
                type="text"
                :placeholder="col.type === 'multiselect' ? 'comma-separated values' : col.title"
              />
            </b-form-group>
          </b-row>
          <div v-if="!editableNewRowColumns.length" class="text-muted">
            No editable columns on this spreadsheet.
          </div>
        </b-form>
      </b-container>
    </b-modal>

    <b-modal v-model="deleteRowsModalVisible" title="delete rows" @ok="deleteSelectedRows" :ok-disabled="deleteRowsSubmitting" ok-variant="danger" ok-title="delete">
      <template #title>
        delete rows
        <modal-help topic="delete-rows" />
      </template>
      <p>Are you sure you want to delete {{ selectedRows.length }} row{{ selectedRows.length === 1 ? '' : 's' }}? This action cannot be undone.</p>
    </b-modal>

    <b-modal v-model="bulkActionsModalVisible" title="bulk actions" size="lg">
      <template #title>
        {{ bulkActionTitle }}
        <modal-help topic="bulk-actions" />
      </template>
      <b-container fluid>
        <div v-if="!bulkActionMode" class="bulk-choice">
          <button type="button" class="bulk-choice-card" @click="chooseBulkValues">
            <span class="bulk-choice-kicker">Update records</span>
            <span class="bulk-choice-title">Update values</span>
            <span class="bulk-choice-text">Write the same field values onto many records. A filter chooses which records. With no filter, every record on this spreadsheet is updated.</span>
          </button>
          <button type="button" class="bulk-choice-card" @click="chooseBulkWorkflow">
            <span class="bulk-choice-kicker">Start a workflow</span>
            <span class="bulk-choice-title">Run a step workflow</span>
            <span class="bulk-choice-text">Pick a workflow by name and run it on the rows you checked. If no rows are checked, a filter chooses the records.</span>
            <span v-if="selectedRows.length" class="bulk-choice-meta">{{ selectedRows.length }} row{{ selectedRows.length === 1 ? '' : 's' }} checked</span>
          </button>
          <button type="button" class="bulk-choice-card" @click="chooseBulkAutomation">
            <span class="bulk-choice-kicker">Run an automation</span>
            <span class="bulk-choice-title">Run an automation</span>
            <span class="bulk-choice-text">Run one automation that uses this spreadsheet. It runs once for each checked row, or for each record that matches a filter.</span>
            <span v-if="selectedRows.length" class="bulk-choice-meta">{{ selectedRows.length }} row{{ selectedRows.length === 1 ? '' : 's' }} checked</span>
          </button>
        </div>
        <template v-else-if="bulkActionMode === 'values'">
        <button type="button" class="bulk-pane-back" @click="bulkActionMode = ''">Back</button>
        <div class="bulk-actions-section">
          <div class="bulk-actions-section-label">Filter — select which records to update</div>
          <runtime-filter-builder
            v-if="bulkActionsModalVisible && spreadSheet"
            :field-groups="filterFieldGroups"
            :initial-filters="[]"
            initial-match-mode="all"
            @filter-changed="onBulkFilterChanged"
          />
          <div class="bulk-actions-match-info">
            <span v-if="bulkActionFilters.length === 0" class="text-muted">No filter set — all records will be updated</span>
            <span v-else-if="bulkActionMatchCount !== null">{{ bulkActionMatchCount }} record{{ bulkActionMatchCount === 1 ? '' : 's' }} match{{ bulkActionMatchCount === 1 ? 'es' : '' }} the filter</span>
            <span v-else class="text-muted">Counting matches...</span>
          </div>
          <div v-if="bulkActionMatchRows.length" class="bulk-actions-match-rows">
            <table class="table table-sm spreadsheet-table bulk-actions-match-table" aria-label="Records included in this bulk action">
              <thead class="spreadsheet-sticky-header">
                <tr>
                  <th v-for="field in tableFields" :key="'bulk-match-' + field.key">{{ field.label }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in bulkActionMatchRows" :key="'bulk-match-row-' + item.id">
                  <td v-for="field in tableFields" :key="field.key">{{ cellFormatHidden(item, field) ? '' : cellDisplayText(item, field) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-if="bulkActionMatchCount !== null && bulkActionMatchCount > bulkActionMatchRows.length" class="bulk-actions-match-more">
            Showing the first {{ bulkActionMatchRows.length }} of {{ bulkActionMatchCount }} records.
          </div>
        </div>
        <hr />
        <div class="bulk-actions-section">
          <div class="bulk-actions-section-label">Values — only filled fields will be updated</div>
          <b-form>
            <b-row v-for="col in editableNewRowColumns" :key="'bulk-' + col.uniqueTitle">
              <b-form-group :label="col.title">
                <b-form-select
                  v-if="col.type === 'multiselect' || col.type === 'relationship'"
                  class="mb-2"
                  :model-value="bulkFieldOp(col)"
                  :options="bulkFieldOpOptions(col)"
                  @update:model-value="onBulkFieldOp(col, $event)"
                />
                <b-form-input
                  v-if="col.type === 'text' || col.type === 'password'"
                  :model-value="bulkActionValues[col.uniqueTitle]"
                  @update:model-value="onBulkValueInput(col.uniqueTitle, $event)"
                  :type="col.type === 'password' ? 'password' : 'text'"
                  :placeholder="'leave empty to keep existing'"
                />
                <b-form-input
                  v-else-if="col.type === 'number'"
                  :model-value="bulkActionValues[col.uniqueTitle]"
                  @update:model-value="onBulkValueInput(col.uniqueTitle, $event)"
                  type="number"
                  :placeholder="'leave empty to keep existing'"
                />
                <b-form-input
                  v-else-if="col.type === 'dateTime'"
                  :model-value="bulkActionValues[col.uniqueTitle]"
                  @update:model-value="onBulkValueInput(col.uniqueTitle, $event)"
                  :type="col.dateOnly ? 'date' : 'datetime-local'"
                  :placeholder="'leave empty to keep existing'"
                />
                <div v-else-if="col.type === 'image' || col.type === 'file'" class="image-file-field">
                  <input v-if="col.type === 'image'" class="form-control form-control-sm" type="file" accept="image/*" @change="onImageFieldFile('bulk', col.uniqueTitle, col.type, $event)" />
                  <input v-else class="form-control form-control-sm" type="file" @change="onImageFieldFile('bulk', col.uniqueTitle, col.type, $event)" />
                  <small v-if="imageUploadName(bulkActionValues[col.uniqueTitle])" class="text-muted d-block mt-1">{{ imageUploadName(bulkActionValues[col.uniqueTitle]) }}</small>
                </div>
                <multiselect-choices
                  v-else-if="col.type === 'multiselect' && hasColumnChoices(col) && bulkFieldOp(col) === 'replace'"
                  :model-value="bulkActionValues[col.uniqueTitle]"
                  :choices="col.choices"
                  :selection-mode="col.selectionMode || 'multiple'"
                  :label="col.title"
                  :radio-name="'bulk-' + col.uniqueTitle"
                  @update:model-value="onBulkValueInput(col.uniqueTitle, $event)"
                />
                <b-form-input
                  v-else-if="col.type === 'multiselect'"
                  :model-value="bulkActionValues[col.uniqueTitle]"
                  @update:model-value="onBulkValueInput(col.uniqueTitle, $event)"
                  type="text"
                  :placeholder="bulkFieldOp(col) === 'replace' ? 'comma-separated values' : 'one value'"
                />
                <b-form-input
                  v-else-if="col.type === 'relationship'"
                  :model-value="bulkActionValues[col.uniqueTitle]"
                  @update:model-value="onBulkValueInput(col.uniqueTitle, $event)"
                  type="number"
                  placeholder="one record id"
                />
                <b-form-input
                  v-else
                  :model-value="bulkActionValues[col.uniqueTitle]"
                  @update:model-value="onBulkValueInput(col.uniqueTitle, $event)"
                  type="text"
                  :placeholder="'leave empty to keep existing'"
                />
              </b-form-group>
            </b-row>
            <div v-if="!editableNewRowColumns.length" class="text-muted">
              No editable columns on this spreadsheet.
            </div>
          </b-form>
        </div>
        <b-alert v-if="bulkActionError" variant="danger" show dismissible @dismissed="bulkActionError = null" class="mt-3">{{ bulkActionError }}</b-alert>
        <b-alert v-if="bulkActionSuccess" variant="success" show dismissible @dismissed="bulkActionSuccess = null" class="mt-3">{{ bulkActionSuccess }}</b-alert>
        </template>
        <template v-else-if="bulkActionMode === 'workflow' || bulkActionMode === 'automation'">
          <button type="button" class="bulk-pane-back" @click="bulkActionMode = ''">Back</button>
          <div class="bulk-actions-section">
            <div class="bulk-actions-section-label">Records</div>
            <p class="bulk-workflow-lead">{{ bulkWorkflowTargetText }}</p>
            <template v-if="selectedRows.length">
              <div v-if="selectedBulkPreviewRows.length" class="bulk-actions-match-rows">
                <table class="table table-sm spreadsheet-table bulk-actions-match-table" aria-label="Checked records that will start the workflow">
                  <thead class="spreadsheet-sticky-header">
                    <tr>
                      <th v-for="field in tableFields" :key="'bulk-selected-' + field.key">{{ field.label }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="item in selectedBulkPreviewRows" :key="'bulk-selected-row-' + item.id">
                      <td v-for="field in tableFields" :key="field.key">{{ cellFormatHidden(item, field) ? '' : cellDisplayText(item, field) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </template>
            <template v-else>
              <runtime-filter-builder
                v-if="bulkActionsModalVisible && spreadSheet"
                :field-groups="filterFieldGroups"
                :initial-filters="[]"
                initial-match-mode="all"
                @filter-changed="onBulkFilterChanged"
              />
              <div class="bulk-actions-match-info">
                <span v-if="!bulkActionFilters.length" class="text-muted">Add a filter. This will not run on every record.</span>
                <span v-else-if="bulkActionMatchCount !== null">{{ bulkActionMatchCount }} record{{ bulkActionMatchCount === 1 ? '' : 's' }} match{{ bulkActionMatchCount === 1 ? 'es' : '' }} the filter</span>
                <span v-else class="text-muted">Counting matches...</span>
              </div>
              <div v-if="bulkActionMatchRows.length" class="bulk-actions-match-rows">
                <table class="table table-sm spreadsheet-table bulk-actions-match-table" aria-label="Records that match the workflow filter">
                  <thead class="spreadsheet-sticky-header">
                    <tr>
                      <th v-for="field in tableFields" :key="'bulk-flow-' + field.key">{{ field.label }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="item in bulkActionMatchRows" :key="'bulk-flow-row-' + item.id">
                      <td v-for="field in tableFields" :key="field.key">{{ cellFormatHidden(item, field) ? '' : cellDisplayText(item, field) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-if="bulkActionMatchCount !== null && bulkActionMatchCount > bulkActionMatchRows.length" class="bulk-actions-match-more">
                Showing the first {{ bulkActionMatchRows.length }} of {{ bulkActionMatchCount }} records. The run includes the first 100.
              </div>
            </template>
          </div>
          <div v-if="bulkActionMode === 'workflow'" class="bulk-actions-section">
            <div class="bulk-actions-section-label">Workflow</div>
            <p v-if="!sheetWorkflows.length" class="bulk-workflow-lead">No step workflow starts from this spreadsheet. Create one on the workflows tab, then come back here.</p>
            <div v-else class="bulk-workflow-list" role="listbox" aria-label="Step workflow to run">
              <button
                v-for="workflow in sheetWorkflows"
                :key="workflow.uniqueTitle"
                type="button"
                class="bulk-workflow-card"
                role="option"
                :aria-selected="bulkWorkflowUniqueTitle === workflow.uniqueTitle"
                :class="{ 'bulk-workflow-card-selected': bulkWorkflowUniqueTitle === workflow.uniqueTitle }"
                @click="bulkWorkflowUniqueTitle = workflow.uniqueTitle"
              >
                <span class="bulk-workflow-card-title">{{ workflow.title }}</span>
                <span class="bulk-workflow-card-id">{{ workflow.uniqueTitle }}</span>
                <span v-if="workflow.description" class="bulk-workflow-card-desc">{{ workflow.description }}</span>
              </button>
            </div>
          </div>
          <p v-if="bulkActionMode === 'workflow' && bulkWorkflowMessage" class="bulk-workflow-message" :class="{ 'bulk-workflow-message-error': bulkWorkflowError }">{{ bulkWorkflowMessage }}</p>
          <div v-if="bulkActionMode === 'automation'" class="bulk-actions-section">
            <div class="bulk-actions-section-label">Automation</div>
            <p v-if="!sheetAutomations.length" class="bulk-workflow-lead">No automation uses this spreadsheet. An automation is listed when it writes these records, saves a response onto the selected record, or fills a placeholder from a field here. Webhooks are not listed, because those wait for an outside call.</p>
            <div v-else class="bulk-workflow-list" role="listbox" aria-label="Automation to run">
              <button
                v-for="automation in sheetAutomations"
                :key="automation.uniqueTitle"
                type="button"
                class="bulk-workflow-card"
                role="option"
                :aria-selected="bulkAutomationUniqueTitle === automation.uniqueTitle"
                :class="{ 'bulk-workflow-card-selected': bulkAutomationUniqueTitle === automation.uniqueTitle }"
                @click="bulkAutomationUniqueTitle = automation.uniqueTitle"
              >
                <span class="bulk-workflow-card-title">{{ automation.title || automation.uniqueTitle }}</span>
                <span class="bulk-workflow-card-id">{{ automationTypeLabel(automation.type) }} · {{ automation.uniqueTitle }}</span>
                <span class="bulk-workflow-card-desc">{{ automation.use.summary }}</span>
              </button>
            </div>
          </div>
          <p v-if="bulkAutomationMessage" class="bulk-workflow-message" :class="{ 'bulk-workflow-message-error': bulkAutomationError }">{{ bulkAutomationMessage }}</p>
        </template>
      </b-container>
      <template #footer>
        <div class="bulk-actions-footer">
          <button v-if="bulkActionMode" type="button" class="btn btn-outline-secondary" @click="bulkActionMode = ''">Back</button>
          <span class="bulk-actions-footer-spacer"></span>
          <button type="button" class="btn btn-secondary" @click="bulkActionsModalVisible = false">Cancel</button>
          <button
            v-if="bulkActionMode === 'values'"
            type="button"
            class="btn btn-primary"
            :disabled="bulkActionSubmitting || !bulkActionHasValues"
            @click="executeBulkAction"
          >{{ bulkActionSubmitting ? 'Applying…' : 'apply' }}</button>
          <button
            v-if="bulkActionMode === 'workflow'"
            type="button"
            class="btn btn-primary"
            :disabled="!canRunBulkWorkflow"
            @click="runBulkWorkflow"
          >{{ bulkWorkflowRunning ? 'Running…' : 'Run workflow' }}</button>
          <button
            v-if="bulkActionMode === 'automation'"
            type="button"
            class="btn btn-primary"
            :disabled="!canRunBulkAutomation"
            @click="runBulkAutomation"
          >{{ bulkAutomationRunning ? 'Running…' : 'Run automation' }}</button>
        </div>
      </template>
    </b-modal>

    <b-modal v-model="importSpreadSheetModalVisible" title="import spreadsheet" size="lg" @ok="doImportSpreadSheet" :ok-disabled="importSpreadSheetSubmitting || !importSpreadSheetReady" ok-title="import">
      <template #title>
        import spreadsheet
        <modal-help topic="import-spreadsheet" />
      </template>
      <b-container fluid>
        <b-form v-if="!importCsvParsed">
          <b-form-group label="CSV file">
            <b-form-file
              v-model="importCsvFile"
              accept=".csv,text/csv"
              placeholder="Choose a CSV file..."
              @change="onImportFileSelected"
            />
          </b-form-group>
        </b-form>
        <div v-else>
          <b-form-group label="spreadsheet title" :invalid-feedback="importSpreadSheetTitleFeedback" :state="importSpreadSheetTitleState">
            <b-form-input v-model="importSpreadSheetTitle" :state="importSpreadSheetTitleState" placeholder="my imported spreadsheet" required />
          </b-form-group>
          <b-form-group label="unique title" :invalid-feedback="importSpreadSheetUniqueTitleFeedback" :state="importSpreadSheetUniqueTitleState">
            <b-form-input :value="importSpreadSheetUniqueTitle" @input="onImportSpreadSheetUniqueTitleInput" :state="importSpreadSheetUniqueTitleState" placeholder="my_imported_spreadsheet" required />
          </b-form-group>
          <b-form-group :label="`columns to import (${importSelectedColumnsCount} selected)`">
            <div class="import-column-list">
              <label
                v-for="col in importColumns"
                :key="col.uniqueTitle"
                class="import-column-item"
              >
                <input type="checkbox" v-model="col.selected" class="import-column-checkbox" />
                <span class="import-column-name">{{ col.title }}</span>
                <span class="import-column-type">{{ col.type }}</span>
              </label>
            </div>
          </b-form-group>
          <p class="text-muted small">{{ importRowCount }} row{{ importRowCount === 1 ? '' : 's' }} will be imported</p>
          <b-form-group v-if="importNeedsTimeZone" label="file time zone">
            <b-form-select v-model="importTimeZone" :options="importTimeZoneOptions" />
            <small class="text-muted">Date and time columns use this zone. The import stops when it is missing.</small>
          </b-form-group>
          <b-form-checkbox v-model="importAcceptInvalid" class="mb-2">Load rows that fail a validation rule</b-form-checkbox>
          <b-alert v-if="importRejected.length" variant="warning" show>
            <div v-for="row in importRejected" :key="row.index">Row {{ row.index + 1 }}: {{ row.message }}</div>
          </b-alert>
          <b-alert v-if="importSpreadSheetError" variant="danger" show dismissible @dismissed="importSpreadSheetError = null">{{ importSpreadSheetError }}</b-alert>
          <b-button v-if="importCsvParsed" size="sm" variant="outline-secondary" @click="resetImportSpreadSheet">choose different file</b-button>
        </div>
      </b-container>
    </b-modal>

    <import-rows
      :visible="rowImportModalVisible"
      @update:visible="rowImportModalVisible = $event"
      :spread-sheet="spreadSheet"
      :application-unique-title="$route.params.application"
      @imported="loadSpreadSheetData"
    />

    <record-history
      :visible="historyModalVisible"
      @update:visible="historyModalVisible = $event"
      :spread-sheet-unique-title="uniqueTitle"
      :record-id="historyRecordId"
      :application-unique-title="$route.params.application"
    />

    <scheduled-tasks
      :visible="scheduledTasksModalVisible"
      @update:visible="scheduledTasksModalVisible = $event"
      :spread-sheet-unique-title="uniqueTitle"
      :spread-sheet="spreadSheet"
      :field-groups="filterFieldGroups"
      :application-unique-title="$route.params.application"
      :automations="availableAutomations"
      :workflows="availableWorkflows"
    />

    <spreadsheet-workflow-hooks
      :visible="workflowHooksModalVisible"
      @update:visible="workflowHooksModalVisible = $event"
      :spread-sheet-unique-title="uniqueTitle"
      :application-unique-title="$route.params.application"
      :spread-sheet="spreadSheet"
      :spreadsheets="spreadsheets"
      :field-groups="filterFieldGroups"
    />

  </b-container>
</template>

<script>

  import axios from 'axios'
  import _ from 'lodash'
  import draggable from 'vuedraggable'
  import FancyGridVue from '../stubs/FancyGridStub.vue';
  import EditableCell from '../EditableCell.vue';
  import MultiselectChoices from '../MultiselectChoices.vue';
  import ImageCell from '../ImageCell.vue';
  import RelationshipEditor from '../RelationshipEditor.vue';
  import RelationshipColumnSettings from '../lib/RelationshipColumnSettings.vue';
  import EditColumnModal from '../EditColumnModal.vue';
  import RuntimeFilterBuilder from '../RuntimeFilterBuilder.vue';
  import RichTextCell from '../RichTextCell.vue';
  import RecordHistory from '../RecordHistory.vue';
  import ScheduledTasks from '../ScheduledTasks.vue';
  import SpreadsheetWorkflowHooks from '../SpreadsheetWorkflowHooks.vue';
  import { browserTimeZone, formatCalendarDate, formatInstant, isCalendarDate, isValidTimeZone, storeDateTime, timeZoneChoices, timeZoneLabel } from '../../../../shared/app-timezone'
  import { applyColumnDefaults, columnSupportsRequired } from '../../../../shared/column-write.js'
  import { onDesignerChange, raiseDesignerWarning } from '../../lib/designer-sync.js'
import { recordFailures } from '../../../../shared/column-validation.js'
  import { imageDataUrl, imageFilename, imageUploadFromDataUrl } from '../../../../shared/image-column'
  import { DURATION_UNIT_OPTIONS, durationBetween, durationUnit, formatDuration } from '../../../../shared/duration-column'
  import SignaturePad from '../SignaturePad.vue'
  import { moveListItem } from '../../../../shared/list-move.js'
  import DragHandle from '../DragHandle.vue'
  import { formatMultiselectDisplay } from '../../lib/multiselect-display.js'
import { formatFieldDisplay } from '../../../../shared/value-format.js';
  import { hasChoices } from '../../lib/multiselect-cell.js';
  import { csvFilename, spreadsheetCsv } from '../../../../shared/spreadsheet-csv.js';
  import { loginSpreadSheetIndex, loginSpreadSheetNote } from '../../../../shared/logged-in-user-default.js';
  import { relationshipUniqueTitleConflict, relationshipUniqueTitleConflictMessage } from '../../../../shared/relationship-column.js';
  import { automationSheetUse, automationRecordVariables, automationTypeShort } from '../../../../shared/automation-integration.js';
  import { applyCreateColumnFormReset, blankCreateColumnForm } from '../../lib/create-column-form.js';
  import { bulkMatchPreview } from '../../lib/bulk-match-preview.js';
  import { rememberDesignerSelection } from '../../lib/designer-selection.js';
  import { columnTypeLabel, showsSeparateId } from '../../lib/designer-labels.js';
  import AiTokenUsage from '../AiTokenUsage.vue';
  import ImportRows from '../ImportRows.vue';

  export default {
    data () {

      return {
        emptyColumnMeta: Object.freeze({}),
        style: `height: ${window.innerHeight - 280}px;overflow: scroll;`,
        containerHeight: window.innerHeight - 100,
        uniqueTitle: ``,
        title: ``,
        spreadSheet: null,
        rowHeaders: [],
        spreadsheets: [],
        loginSpreadSheets: {},
        spreadsheetsLoadError: null,
        items: [],
        csvExporting: false,
        appTimeZone: 'UTC',
        newSpreadSheetTitle: ``,
        newSpreadSheetUniqueTitle: ``,
        newSpreadSheetUniqueTitleManuallyEdited: false,
        newSpreadSheetSubmitAttempted: false,
        ...blankCreateColumnForm(),
        createColumnFormKey: 0,
        newColumnServerError: '',
        editColumnServerError: '',
        editColumnData: {},
        editSpreadSheetUniqueColumnTitle: ``,
        deleteSpreadSheetTitle: ``,
        deleteColumnTitle: ``,
        createSpreadSheetModalVisible: false,
        deleteSpreadSheetModalVisible: false,
        createSpreadSheetColumnModalVisible: false,
        editSpreadSheetColumnModalVisible: false,
        deleteColumnModalVisible: false,
        newRowModalVisible: false,
        newRowData: {},
        newRowRelState: {},
        newRowSubmitting: false,
        newRowError: null,
        newRelationshipSettings: {
          displayFormula: '',
          cascadeFilters: [],
          cascadeMatchMode: 'all',
          showAllUntilParent: false,
          allowAdd: false,
          addFields: []
        },
        gridWriteError: null,
        columnTypes: [
          {value: `number`, text: `number`},
          {value: `text`, text: `text`},
          {value: `password`, text: `password`},
          {value: `dateTime`, text: columnTypeLabel(`dateTime`)},
          {value: `relationship`, text: `relationship`},
          {value: `image`, text: `image`},
          {value: `file`, text: `file`},
          {value: `formula`, text: `formula`},
          {value: `richText`, text: columnTypeLabel(`richText`)},
          {value: `multiselect`, text: `multiselect`},
          {value: `address`, text: `address`},
          {value: `signature`, text: `signature`},
          {value: `duration`, text: `duration`},
          {value: `sequence`, text: `sequence`}
        ],
        durationUnitOptions: DURATION_UNIT_OPTIONS,
        selectionModeOptions: [
          {value: `single`, text: `single select`},
          {value: `multiple`, text: `multiple select`}
        ],
        spreadSheetOptions: [],
        selectedRows: [],
        deleteRowsModalVisible: false,
        deleteRowsSubmitting: false,
        activeJobs: [],
        editingCellKey: null,
        virtualScrollTop: 0,
        virtualRowHeight: 38,
        virtualBufferRows: 20,
        showFilters: false,
        currentFilters: [],
        currentFilterMatchMode: 'all',
        ws: null,
        importSpreadSheetModalVisible: false,
        importCsvFile: null,
        importCsvParsed: false,
        importColumns: [],
        importRows: [],
        importSpreadSheetTitle: ``,
        importSpreadSheetUniqueTitle: ``,
        importSpreadSheetUniqueTitleManuallyEdited: false,
        importSpreadSheetSubmitting: false,
        importSpreadSheetError: null,
        importAcceptInvalid: false,
        importTimeZone: '',
        newSpreadSheetColumnDateOnly: false,
        importRejected: [],
        rowImportModalVisible: false,
        historyModalVisible: false,
        historyRecordId: null,
        copySpreadSheetModalVisible: false,
        copySpreadSheetTitle: ``,
        copySpreadSheetUniqueTitle: ``,
        copySpreadSheetUniqueTitleManuallyEdited: false,
        copySpreadSheetSubmitAttempted: false,
        copySpreadSheetSubmitting: false,
        copySpreadSheetWithRecords: false,
        copySpreadSheetError: null,
        bulkActionsModalVisible: false,
        bulkActionMode: '',
        bulkActionFilters: [],
        bulkActionMatchMode: 'all',
        bulkActionValues: {},
        bulkFieldOps: {},
        bulkActionSubmitting: false,
        bulkActionMatchCount: null,
        bulkActionMatchRows: [],
        bulkActionMatchRequestId: 0,
        bulkActionError: null,
        bulkActionSuccess: null,
        scheduledTasksModalVisible: false,
        workflowHooksModalVisible: false,
        availableAutomations: [],
        availableWorkflows: [],
        bulkWorkflowUniqueTitle: '',
        bulkWorkflowRunning: false,
        bulkWorkflowMessage: '',
        bulkWorkflowError: false,
        bulkAutomationUniqueTitle: '',
        bulkAutomationRunning: false,
        bulkAutomationMessage: '',
        bulkAutomationError: false,
        createSpreadSheetActiveTab: 0,
        aiSpreadSheetPrompt: ``,
        aiSpreadSheetError: null,
        aiSpreadSheetSuccess: null,
        createSpreadSheetAiSubmitting: false,
        createSpreadSheetColumnsWithAiModalVisible: false,
        aiColumnsPrompt: ``,
        aiColumnsError: null,
        aiColumnsSuccess: null,
        aiColumnsSubmitting: false
      }
    },
    watch: {
      '$route.params.application' () {
        this.spreadsheets = []
        this.loginSpreadSheets = {}
        this.title = ''
        this.uniqueTitle = ''
        this.spreadSheet = null
        this.rows = []
        this.selectedRows = []
        this.getSpreadSheets()
      },
      newSpreadSheetTitle (val) {
        if (!this.newSpreadSheetUniqueTitleManuallyEdited) {
          this.newSpreadSheetUniqueTitle = this.sanitizeToUniqueTitle(val)
        }
      },
      newSpreadSheetColumnTitle (val) {
        if (!this.newColumnUniqueTitleManuallyEdited) {
          this.newSpreadSheetUniqueColumnTitle = this.sanitizeToUniqueTitle(val)
        }
      },
      newSpreadSheetUniqueColumnTitle () {
        this.newColumnServerError = ''
      },
      newSpreadSheetUniqueColumnType () {
        this.newColumnServerError = ''
      },
      importSpreadSheetTitle (val) {
        if (!this.importSpreadSheetUniqueTitleManuallyEdited) {
          this.importSpreadSheetUniqueTitle = this.sanitizeToUniqueTitle(val)
        }
      },
      copySpreadSheetTitle (val) {
        if (!this.copySpreadSheetUniqueTitleManuallyEdited) {
          this.copySpreadSheetUniqueTitle = this.sanitizeToUniqueTitle(val)
        }
      }
    },
    async mounted () {

      await this.loadAppTimeZone()
      this.getSpreadSheets()
      this.connectWebSocket()
      this._stopDesignerSync = onDesignerChange((change) => this.applyDesignerChange(change))

      this._onResize = _.debounce(() => {
        this.style = `height: ${window.innerHeight - 280}px;overflow: scroll;`
        this.containerHeight = window.innerHeight - 100
      }, 150)
      window.addEventListener(`resize`, this._onResize)
    },
    beforeUnmount () {

      if (this._onResize) {
        window.removeEventListener(`resize`, this._onResize)
      }
      if (this.ws) {
        this.ws.close()
        this.ws = null
      }
      if (this._stopDesignerSync) this._stopDesignerSync()
    },
    methods: {

      columnTypeLabel,
      showsSeparateId,
      sanitizeToUniqueTitle (title) {
        return title
          .toLowerCase()
          .replace(/[^a-z0-9\s_]/g, '')
          .replace(/\s+/g, '_')
          .replace(/_+/g, '_')
          .replace(/^_|_$/g, '')
      },
      onSpreadSheetUniqueTitleInput (val) {
        const value = (val && val.target) ? val.target.value : val
        this.newSpreadSheetUniqueTitle = value
        this.newSpreadSheetUniqueTitleManuallyEdited = !!value.trim()
      },
      onUniqueColumnTitleInput (val) {
        const value = (val && val.target) ? val.target.value : val
        this.newSpreadSheetUniqueColumnTitle = value
        this.newColumnUniqueTitleManuallyEdited = !!value.trim()
      },
      spreadsheetEditorOpen () {

        return this.createSpreadSheetColumnModalVisible
          || this.createSpreadSheetColumnsWithAiModalVisible
          || this.editSpreadSheetColumnModalVisible
          || this.deleteColumnModalVisible
          || this.workflowHooksModalVisible
      },
      recordEditorOpen () {

        return !!this.editingCellKey
          || this.newRowModalVisible
          || this.bulkActionsModalVisible
          || this.deleteRowsModalVisible
          || this.rowImportModalVisible
      },
      async applyDesignerChange (change) {

        const id = this.uniqueTitle
        const keys = change && change.keys || []
        const appWide = keys.includes('application')
        const schemaHit = appWide || keys.includes('spreadsheets') || (id && keys.includes(`spreadsheet:${id}`))
        const recordsHit = appWide || (id && keys.includes(`records:${id}`))

        if (!schemaHit && !recordsHit) return

        if (schemaHit && this.spreadsheetEditorOpen()) {

          raiseDesignerWarning(change, 'this spreadsheet')

          return
        }

        if (recordsHit && this.recordEditorOpen()) {

          raiseDesignerWarning(change, 'records in this spreadsheet')

          return
        }

        if (schemaHit) {

          await this.getSpreadSheets()

          return
        }

        if (recordsHit) await this.loadSpreadSheetData()
      },
      connectWebSocket () {

        const wsUrl = VUE_APP_APP_SERVER_WS_URL

        try {
          this.ws = new WebSocket(wsUrl)
        } catch (err) {
          console.warn('WebSocket connection failed:', err)
          return
        }

        this.ws.onmessage = (event) => {

          let msg
          try {
            msg = JSON.parse(event.data)
          } catch {
            return
          }

          if (msg.spreadSheetUniqueTitle !== this.uniqueTitle) return

          if (msg.type === 'job_progress') {

            const idx = this.activeJobs.findIndex(j => j.jobId === msg.jobId)

            if (idx >= 0) {
              this.activeJobs.splice(idx, 1, msg)
            } else {
              this.activeJobs.push(msg)
            }
          } else if (msg.type === 'job_complete') {

            this.activeJobs = this.activeJobs.filter(j => j.jobId !== msg.jobId)
            this.loadSpreadSheetData()
          } else if (msg.type === 'job_failed') {

            this.activeJobs = this.activeJobs.filter(j => j.jobId !== msg.jobId)
            console.warn(`Job ${msg.jobId} failed: ${msg.errorMessage}`)
          }
        }

        this.ws.onclose = () => {

          setTimeout(() => {
            if (!this._isBeingDestroyed && !this._isDestroyed) {
              this.connectWebSocket()
            }
          }, 3000)
        }

        this.ws.onerror = () => {}
      },
      jobLabel (job) {

        if (job.jobType === 'formula_recalc') {
          return `Recalculating formula: ${job.columnUniqueTitle} (${job.processedItems || 0}/${job.totalItems || 0})`
        }

        return `Processing job ${job.jobId}`
      },
      isRelationshipField (fieldKey) {
        const fm = this.fieldMap[fieldKey]
        return fm && fm.type === 'relationship'
      },
      getRelationshipValue (item, fieldKey) {
        // The relationship data comes back as fieldKey_from from the Sequelize include
        const val = item[fieldKey + '_from']
        return Array.isArray(val) ? val : []
      },
      getColumnMeta (fieldKey) {
        return this.columnMetaMap[fieldKey] || this.emptyColumnMeta
      },
      columnChoices (fieldKey) {
        const column = this.columnMetaMap[fieldKey]
        return column && Array.isArray(column.choices) ? column.choices : []
      },
      columnSelectionMode (fieldKey) {
        const column = this.columnMetaMap[fieldKey]
        return (column && column.selectionMode) || 'multiple'
      },
      hasColumnChoices (column) {
        return hasChoices(column && column.choices)
      },
      async loadAppTimeZone () {

        try {

          const res = await axios.get(`${VUE_APP_PROXY_SERVER_URL}/settings`, {
            headers: { 'application-unique-title': this.$route.params.application }
          })

          this.appTimeZone = (res.data && res.data.timeZone) || 'UTC'
        } catch (err) {

          this.appTimeZone = 'UTC'
        }
      },
      formatCellValue (value, field) {
        if (value == null || value === '') return '—'
        if (typeof value === 'object') return '[object]'
        if (field && field.dateOnly || (typeof value === 'string' && isCalendarDate(value) && field && field.type === 'dateTime')) {
          return formatCalendarDate(value) || String(value)
        }
        if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
          return formatInstant(value, browserTimeZone()) || value
        }
        return String(value)
      },
      onCellClick (item, field) {
        const fm = this.fieldMap[field.key]
        if (!fm) return
        if (fm.editable === false && field.key !== 'id' && fm.type !== 'relationship' && fm.type !== 'richText') return
        this.editingCellKey = item.id + '::' + field.key
      },
      updateCellAndClear (payload) {
        this.editingCellKey = null
        this.updateCell(payload)
      },
      onRelationshipChanged () {
        this.editingCellKey = null
        this.loadSpreadSheetData()
      },
      onTableScroll (e) {
        this.virtualScrollTop = e.target.scrollTop
      },
      cellClasses (item, field) {
        const classes = {}
        classes['cell-display-readonly'] = !this.fieldMap[field.key] || this.fieldMap[field.key].editable === false || field.key === 'id'
        classes['cell-display-richtext'] = this.fieldMap[field.key] && this.fieldMap[field.key].type === 'richText'
        return classes
      },
      cellFormatStyle (item, field) {
        const style = {}
        const display = formatFieldDisplay(item[field.key], field.valueFormatting)
        if (display && display.style) Object.assign(style, display.style)
        if (!item._formatting || !item._formatting[field.key]) return Object.keys(style).length ? style : null
        const fmt = item._formatting[field.key]
        if (fmt.color) style.color = fmt.color
        if (fmt.bgColor) style.backgroundColor = fmt.bgColor
        if (fmt.bold) style.fontWeight = 'bold'
        if (fmt.italic) style.fontStyle = 'italic'
        if (fmt.fontSize) style.fontSize = fmt.fontSize + 'px'
        if (fmt.borderColor) {
          style.border = `${fmt.borderWidth || 1}px solid ${fmt.borderColor}`
        }
        if (fmt.customCss) {
          const pairs = fmt.customCss.split(';').filter(p => p.trim())
          for (const pair of pairs) {
            const colonIdx = pair.indexOf(':')
            if (colonIdx > 0) {
              const prop = pair.substring(0, colonIdx).trim()
              const val = pair.substring(colonIdx + 1).trim()
              // Convert kebab-case to camelCase for Vue inline styles
              const camelProp = prop.replace(/-([a-z])/g, (_, c) => c.toUpperCase())
              style[camelProp] = val
            }
          }
        }
        return style
      },
      cellFormatHidden (item, field) {
        return item._formatting && item._formatting[field.key] && item._formatting[field.key].hidden
      },
      cellImageUrl (item, field) {
        const fm = this.fieldMap[field.key]
        if (!fm || (fm.type !== 'image' && fm.type !== 'signature')) return ''
        return imageDataUrl(item, field.key)
      },
      cellImageAlt (item, field) {
        return imageFilename(item, field.key) || field.label || 'Image'
      },
      cellAccessibleName (item, field) {
        const value = this.cellFormatHidden(item, field) ? 'hidden' : this.cellDisplayText(item, field)
        return `${field.label}: ${value}`
      },
      cellDisplayText (item, field) {
        const fm = this.fieldMap[field.key]
        const value = item[field.key]
        if (!fm) return this.formatCellValue(value, field)
        if (fm.type === 'password') return (value == null || value === '') ? '—' : '••••••••'
        if (fm.type === 'image' || fm.type === 'file' || fm.type === 'signature') return imageFilename(item, field.key) || '—'
        if (fm.type === 'duration') return formatDuration(value, fm.durationUnit || (this.columnMetaMap[field.key] && this.columnMetaMap[field.key].durationUnit)) || '—'
        if (fm.type === 'sequence') return value == null || value === '' ? '—' : String(value)
        if (fm.type === 'relationship') {
          const val = item[field.key + '_from']
          if (!Array.isArray(val) || val.length === 0) return '—'
          return val.map(rec => {
            if (typeof rec === 'object' && rec !== null) {
              const dk = Object.keys(rec).find(k => k !== 'id' && k !== 'createdAt' && k !== 'updatedAt' && !k.endsWith('_from') && !k.endsWith('_to'))
              return dk && rec[dk] != null ? String(rec[dk]) : `#${rec.id}`
            }
            return `#${rec}`
          }).join(', ')
        }
        if (fm.type === 'multiselect') {
          const text = formatMultiselectDisplay(value)
          return text || '—'
        }
        const display = formatFieldDisplay(value, fm.valueFormatting)
        if (display) return display.text || '—'
        if (fm.type === 'richText') {
          if (value == null || value === '' || typeof value !== 'string') return '—'
          let preview = value.replace(/<img[^>]*alt="([^"]*)"[^>]*>/gi, ' [$1] ')
          preview = preview.replace(/<img[^>]*>/gi, ' [image] ')
          preview = preview.replace(/<a[^>]*>(.*?)<\/a>/gi, ' $1 ')
          preview = preview.replace(/<[^>]*>/g, '').trim()
          preview = preview.replace(/\s+/g, ' ')
          if (!preview) return '—'
          return preview.length > 50 ? preview.substring(0, 50) + '...' : preview
        }
        return this.formatCellValue(value, fm)
      },
      async updateCell ({ item, fieldKey, newValue }) {
        const values = Object.assign({}, item, { [fieldKey]: newValue })
        const failures = recordFailures(this.spreadSheet && this.spreadSheet.columns, values, null, { only: new Set([fieldKey]) })

        if (failures.length) {
          this.gridWriteError = failures[0].message
          return
        }

        const updatePayload = { [fieldKey]: newValue }
        try {
          const response = await this.$http.put(
            `${VUE_APP_PROXY_SERVER_URL}/data/${this.uniqueTitle}/${item.id}`,
            updatePayload,
            { headers: { 'application-unique-title': this.$route.params.application } }
          )
          const idx = this.items.findIndex((i) => i.id === item.id)
          if (idx >= 0) {
            const updatedRecord = response.data
            const newItems = [...this.items]
            newItems[idx] = Object.freeze({ ...this.items[idx], ...updatedRecord })
            this.items = Object.freeze(newItems)
          }
          this.gridWriteError = null
        } catch (error) {
          const data = error && error.response && error.response.data
          this.gridWriteError = typeof data === 'string' && data.trim() ? data : 'Could not save this cell.'
          console.error('Failed to update cell:', error)
        }
      },
      async updateRow (row, prop, oldValue, newValue) {},
      async createNewRow (index) {

        const data = this.$refs.hotTableComponent.hotInstance.getDataAtRow(index - 1)

        const body = {}
        let counter = 0

        for (const column of this.spreadSheet.columns) {

          body[column.uniqueTitle] = data[counter]
          counter++
        }

        try {

          await this.$http.post(`${VUE_APP_PROXY_SERVER_URL}/data/${this.uniqueTitle}`, body, {headers: {'application-unique-title': this.$route.params.application}})
        } catch (error) {

          console.error(`error creating new data:`, error)
        }

        this.loadSpreadSheetData()
      },
      async loadSpreadSheet (spreadSheet) {

        if (!spreadSheet || !spreadSheet.columns) {
          console.warn('loadSpreadSheet: invalid or missing spreadsheet data')
          return
        }

        this.uniqueTitle = spreadSheet ? spreadSheet.uniqueTitle : ``
        this.title = spreadSheet.title
        rememberDesignerSelection(window.sessionStorage, this.$route.params.application, {
          spreadsheet: this.uniqueTitle
        })
        this.selectedRows = []
        this.currentFilters = []
        this.currentFilterMatchMode = 'all'
        this.spreadSheet = spreadSheet

        // Update URL to enable deep linking
        const targetPath = `/${this.$route.params.application}/spreadsheets/${this.uniqueTitle}`
        if (this.$route.path !== targetPath) {
          this.$router.replace(targetPath)
        }

        await this.loadAvailableWorkflows()
        await this.loadSpreadSheetData()
        this.loadActiveJobs()
      },
      async loadActiveJobs () {

        try {

          const response = await this.$http.get(
            `${VUE_APP_PROXY_SERVER_URL}/jobs/spreadsheet/${this.uniqueTitle}`,
            { headers: { 'application-unique-title': this.$route.params.application } }
          )

          this.activeJobs = (response.data || []).map(j => ({
            jobId: j.id,
            jobType: j.type,
            spreadSheetUniqueTitle: j.payload ? j.payload.spreadSheetUniqueTitle : '',
            columnUniqueTitle: j.payload ? j.payload.columnUniqueTitle : '',
            processedItems: j.processedItems || 0,
            totalItems: j.totalItems || 0,
            progress: j.progress || 0,
            status: j.status
          }))
        } catch (err) {

          console.warn('Failed to load active jobs:', err)
        }
      },
      onFilterChanged (filterEvent) {
        this.currentFilters = filterEvent.filters || []
        this.currentFilterMatchMode = filterEvent.matchMode || 'all'
        this.loadSpreadSheetData()
      },
      exportSpreadsheetCsv () {
        if (this.csvExporting || !this.spreadSheet) return

        this.csvExporting = true

        try {
          const csv = spreadsheetCsv(this.tableFields, this.items)
          const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
          const url = URL.createObjectURL(blob)
          const link = document.createElement('a')

          link.href = url
          link.download = csvFilename(this.title || this.uniqueTitle)
          document.body.appendChild(link)
          link.click()
          link.remove()
          URL.revokeObjectURL(url)
        } finally {
          this.csvExporting = false
        }
      },
      async loadSpreadSheetData () {

        let url = `${VUE_APP_PROXY_SERVER_URL}/data/${this.uniqueTitle}`

        if (this.currentFilters && this.currentFilters.length > 0) {
          url += `?userFilters=${encodeURIComponent(JSON.stringify(this.currentFilters))}&userFilterMatchMode=${encodeURIComponent(this.currentFilterMatchMode)}`
        }

        const data = await this.$http.get(url, {headers: {'application-unique-title': this.$route.params.application}})

        // Freeze row data to prevent Vue from creating deep reactive proxies
        // for every property of every row. This is the single biggest perf win
        // for large datasets.
        const rows = data.data
        if (Array.isArray(rows)) {
          for (let i = 0; i < rows.length; i++) {
            Object.freeze(rows[i])
          }
        }
        this.items = Object.freeze(rows)
      },
      loginPagesFor (spreadsheet) {

        const id = spreadsheet && spreadsheet.uniqueTitle

        return (id && this.loginSpreadSheets[id]) || []
      },
      loginNoteFor (spreadsheet) {

        return loginSpreadSheetNote(this.loginPagesFor(spreadsheet))
      },
      async getSpreadSheets () {

        this.spreadsheetsLoadError = null

        try {

          const headers = {'application-unique-title': this.$route.params.application}
          const [spreadsheets, pages] = await Promise.all([
            this.$http.get(`${VUE_APP_PROXY_SERVER_URL}/spreadsheets/`, {headers}),
            this.$http.get(`${VUE_APP_PROXY_SERVER_URL}/pages/`, {headers}).catch(() => ({ data: [] }))
          ])

          this.spreadsheets = spreadsheets.data
          this.loginSpreadSheets = loginSpreadSheetIndex(pages.data)

          this.spreadSheetOptions = (this.spreadsheets || []).map((spreadsheet) => {

            return {
              value: spreadsheet.uniqueTitle,
              text: spreadsheet.title
            }
          })

          //this.gridConfig.data = [[]]

          if (this.spreadsheets && this.spreadsheets.length > 0) {
            const targetUniqueTitle = this.$route.params.spreadsheet
            const target = targetUniqueTitle
              ? this.spreadsheets.find(s => s.uniqueTitle === targetUniqueTitle)
              : null
            this.loadSpreadSheet(target || this.spreadsheets[0])
          }
        } catch (err) {

          console.error('getSpreadSheets failed:', err)
          this.spreadsheets = []
          this.loginSpreadSheets = {}
          this.spreadSheetOptions = []
          this.spreadsheetsLoadError = err.response
            ? `Server error: ${err.response.status}`
            : (err.message || 'Network error. Is the proxy server running on port 16666?')
        }
      },
      dismissCellEditors () {
        this.editingCellKey = null
        document.dispatchEvent(new CustomEvent('cell-editor-open', { detail: 'modal' }))
      },
      showCreateSpreadSheet () {

        this.dismissCellEditors()
        this.newSpreadSheetTitle = ``
        this.newSpreadSheetUniqueTitle = ``
        this.newSpreadSheetUniqueTitleManuallyEdited = false
        this.newSpreadSheetSubmitAttempted = false
        this.createSpreadSheetActiveTab = 0
        this.aiSpreadSheetPrompt = ``
        this.aiSpreadSheetError = null
        this.aiSpreadSheetSuccess = null
        this.createSpreadSheetAiSubmitting = false
        this.createSpreadSheetModalVisible = true
      },
      showCreateSpreadSheetColumnsWithAi () {

        this.dismissCellEditors()

        if (!this.uniqueTitle) {

          return
        }

        this.aiColumnsPrompt = ``
        this.aiColumnsError = null
        this.aiColumnsSuccess = null
        this.aiColumnsSubmitting = false
        this.createSpreadSheetColumnsWithAiModalVisible = true
      },
      onCreateSpreadSheetModalOk (event) {

        if (this.createSpreadSheetActiveTab === 1) {

          return this.createSpreadSheetWithAi(event)
        }

        return this.createSpreadSheet(event)
      },
      async createSpreadSheetWithAi (event) {

        event.preventDefault()

        const prompt = (this.aiSpreadSheetPrompt || ``).trim()

        if (!prompt.length) {

          this.aiSpreadSheetError = `Please describe the spreadsheet you want to create.`
          return
        }

        this.aiSpreadSheetError = null
        this.aiSpreadSheetSuccess = null
        this.createSpreadSheetAiSubmitting = true

        try {

          const response = await this.$http.post(
            `${VUE_APP_PROXY_SERVER_URL}/ai-spreadsheets/create`,
            { prompt },
            {
              headers: {
                'Content-Type': 'application/json',
                'application-unique-title': this.$route.params.application
              },
              timeout: 600000
            }
          )

          const created = response?.data?.spreadsheet
          const failed = (created && created._aiColumnsFailed) || []
          const createdCols = (created && created._aiColumnsCreated) || []

          this.aiSpreadSheetSuccess = `Created "${created?.title || 'spreadsheet'}" with ${createdCols.length} column${createdCols.length === 1 ? '' : 's'}${failed.length ? ` (${failed.length} failed)` : ''}.`
          this.createSpreadSheetModalVisible = false

          await this.getSpreadSheets()

          if (created && created.uniqueTitle) {

            const fresh = this.spreadsheets.find(s => s.uniqueTitle === created.uniqueTitle)
            if (fresh) this.loadSpreadSheet(fresh)
          }
        } catch (error) {

          console.error(`AI create spreadsheet failed:`, error)
          const data = error?.response?.data
          this.aiSpreadSheetError = (typeof data === 'string' && data) || error?.message || `AI request failed`
        }

        this.createSpreadSheetAiSubmitting = false
      },
      async createSpreadSheetColumnsWithAi (event) {

        event.preventDefault()

        const prompt = (this.aiColumnsPrompt || ``).trim()

        if (!prompt.length) {

          this.aiColumnsError = `Please describe the columns you want to add.`
          return
        }

        if (!this.uniqueTitle) {

          this.aiColumnsError = `No spreadsheet selected.`
          return
        }

        this.aiColumnsError = null
        this.aiColumnsSuccess = null
        this.aiColumnsSubmitting = true

        try {

          const response = await this.$http.post(
            `${VUE_APP_PROXY_SERVER_URL}/ai-spreadsheets/${this.uniqueTitle}/columns`,
            { prompt },
            {
              headers: {
                'Content-Type': 'application/json',
                'application-unique-title': this.$route.params.application
              },
              timeout: 600000
            }
          )

          const createdColumns = response?.data?.createdColumns || []
          const failedColumns = response?.data?.failedColumns || []

          if (failedColumns.length) {
            this.aiColumnsError = failedColumns.map((failed) => {
              const name = failed.column && (failed.column.title || failed.column.uniqueTitle)
              return name ? `${name}: ${failed.error}` : failed.error
            }).join(' ')
            if (createdColumns.length) {
              this.aiColumnsSuccess = `Added ${createdColumns.length} column${createdColumns.length === 1 ? '' : 's'}.`
              await this.getSpreadSheets()
              const partial = this.spreadsheets.find(s => s.uniqueTitle === this.uniqueTitle)
              if (partial) this.loadSpreadSheet(partial)
            }
            return
          }

          this.aiColumnsSuccess = `Added ${createdColumns.length} column${createdColumns.length === 1 ? '' : 's'}.`
          this.createSpreadSheetColumnsWithAiModalVisible = false

          await this.getSpreadSheets()
          const fresh = this.spreadsheets.find(s => s.uniqueTitle === this.uniqueTitle)
          if (fresh) this.loadSpreadSheet(fresh)
        } catch (error) {

          console.error(`AI add columns failed:`, error)
          const data = error?.response?.data
          this.aiColumnsError = (typeof data === 'string' && data) || error?.message || `AI request failed`
        }

        this.aiColumnsSubmitting = false
      },
      showImportSpreadSheet () {

        this.dismissCellEditors()
        this.resetImportSpreadSheet()
        this.importSpreadSheetModalVisible = true
      },
      showImportRows () {

        this.dismissCellEditors()
        this.rowImportModalVisible = true
      },
      showCopySpreadSheet () {

        this.dismissCellEditors()

        if (!this.uniqueTitle) return

        this.copySpreadSheetTitle = `Copy of ${this.title}`
        this.copySpreadSheetUniqueTitle = this.sanitizeToUniqueTitle(`copy_of_${this.uniqueTitle}`)
        this.copySpreadSheetUniqueTitleManuallyEdited = false
        this.copySpreadSheetSubmitAttempted = false
        this.copySpreadSheetSubmitting = false
        this.copySpreadSheetWithRecords = false
        this.copySpreadSheetError = null
        this.copySpreadSheetModalVisible = true
      },
      onCopySpreadSheetUniqueTitleInput (val) {

        const value = (val && val.target) ? val.target.value : val
        this.copySpreadSheetUniqueTitle = value
        this.copySpreadSheetUniqueTitleManuallyEdited = !!value.trim()
      },
      async copySpreadSheet (event) {

        this.copySpreadSheetSubmitAttempted = true

        if (!String(this.copySpreadSheetTitle).trim().length || !String(this.copySpreadSheetUniqueTitle).trim().length || this.copySpreadSheetUniqueTitleCollision) {
          return event.preventDefault()
        }

        event.preventDefault()
        this.copySpreadSheetSubmitting = true
        this.copySpreadSheetError = null

        try {

          await this.$http.post(`${VUE_APP_PROXY_SERVER_URL}/spreadsheets/${this.uniqueTitle}/copy`, {
            title: this.copySpreadSheetTitle,
            uniqueTitle: this.copySpreadSheetUniqueTitle,
            copyRecords: this.copySpreadSheetWithRecords
          }, {
            headers: {
              'Content-Type': 'application/json',
              'application-unique-title': this.$route.params.application
            }
          })

          this.copySpreadSheetModalVisible = false
          await this.getSpreadSheets()
          const newSheet = this.spreadsheets.find(s => s.uniqueTitle === this.copySpreadSheetUniqueTitle)
          if (newSheet) this.loadSpreadSheet(newSheet)
        } catch (error) {

          console.error('Copy spreadsheet failed:', error)
          const msg = error?.response?.data || error?.message || 'Copy failed'
          this.copySpreadSheetError = typeof msg === 'string' ? msg : JSON.stringify(msg)
        }

        this.copySpreadSheetSubmitting = false
      },
      resetImportSpreadSheet () {

        this.importCsvFile = null
        this.importCsvParsed = false
        this.importColumns = []
        this.importRows = []
        this.importSpreadSheetTitle = ``
        this.importSpreadSheetUniqueTitle = ``
        this.importSpreadSheetUniqueTitleManuallyEdited = false
        this.importSpreadSheetError = null
        this.importAcceptInvalid = false
        this.importTimeZone = ''
        this.importRejected = []
      },
      onImportSpreadSheetUniqueTitleInput (val) {

        const value = (val && val.target) ? val.target.value : val
        this.importSpreadSheetUniqueTitle = value
        this.importSpreadSheetUniqueTitleManuallyEdited = !!value.trim()
      },
      onImportFileSelected (event) {

        const file = event?.target?.files?.[0]
        if (!file) return

        const reader = new FileReader()
        reader.onload = (e) => {

          const text = e.target?.result
          if (!text || typeof text !== 'string') return

          const { headers, rows } = this.parseCSV(text)
          if (!headers.length) {
            console.warn('No headers found in CSV')
            return
          }

          const seen = {}
          const columns = headers.map((title, idx) => {

            let uniqueTitle = this.sanitizeToUniqueTitle(title) || `column_${idx + 1}`
            if (seen[uniqueTitle]) {
              let n = 1
              while (seen[uniqueTitle + '_' + n]) n++
              uniqueTitle = uniqueTitle + '_' + n
            }
            seen[uniqueTitle] = true

            const sampleValues = rows.slice(0, Math.min(20, rows.length)).map(r => r[idx])
            const type = this.inferColumnType(sampleValues)

            return {
              title: title || uniqueTitle,
              uniqueTitle,
              type,
              selected: true
            }
          })

          this.importColumns = columns
          this.importRows = rows
          this.importCsvParsed = true
          this.importSpreadSheetTitle = file.name.replace(/\.csv$/i, '').replace(/[^a-zA-Z0-9_\s-]/g, ' ').trim() || 'imported'
          this.importSpreadSheetUniqueTitle = this.sanitizeToUniqueTitle(this.importSpreadSheetTitle) || 'imported'
          this.importSpreadSheetUniqueTitleManuallyEdited = false
        }
        reader.readAsText(file, 'UTF-8')
      },
      parseCSV (text) {

        const lines = text.split(/\r?\n/).filter(line => line.trim().length > 0)
        if (!lines.length) return { headers: [], rows: [] }

        const parseLine = (line) => {

          const result = []
          let current = ''
          let inQuotes = false
          for (let i = 0; i < line.length; i++) {
            const c = line[i]
            if (c === '"') {
              inQuotes = !inQuotes
            } else if (inQuotes) {
              current += c
            } else if (c === ',') {
              result.push(current.trim().replace(/^"|"$/g, ''))
              current = ''
            } else {
              current += c
            }
          }
          result.push(current.trim().replace(/^"|"$/g, ''))
          return result
        }

        const headers = parseLine(lines[0])
        const rows = lines.slice(1).map(parseLine)
        return { headers, rows }
      },
      inferColumnType (sampleValues) {

        const nonEmpty = sampleValues.filter(v => v != null && String(v).trim() !== '')
        if (!nonEmpty.length) return 'text'

        let allNumbers = true
        let allDates = true
        const isoDate = /^\d{4}-\d{2}-\d{2}(T|\s)/

        for (const v of nonEmpty) {
          const s = String(v).trim()
          if (s === '' || isNaN(Number(s)) || s.includes(',')) allNumbers = false
          if (!isoDate.test(s) && isNaN(Date.parse(s))) allDates = false
        }

        if (allNumbers) return 'number'
        if (allDates) return 'dateTime'
        return 'text'
      },
      async doImportSpreadSheet (event) {

        if (!this.importSpreadSheetReady) {
          event.preventDefault()
          return
        }

        const selectedCols = this.importColumns.filter(c => c.selected)
        if (!selectedCols.length) {
          event.preventDefault()
          return
        }

        event.preventDefault()
        if (this.importNeedsTimeZone && !isValidTimeZone(this.importTimeZone)) {
          this.importSpreadSheetError = 'Import needs the time zone of the file for date and time columns.'
          return
        }
        this.importSpreadSheetSubmitting = true
        this.importSpreadSheetError = null

        try {

          const body = {
            title: this.importSpreadSheetTitle,
            uniqueTitle: this.importSpreadSheetUniqueTitle
          }

          await this.$http.post(`${VUE_APP_PROXY_SERVER_URL}/spreadsheets/`, body, {
            headers: {
              'Content-Type': 'application/json',
              'application-unique-title': this.$route.params.application
            }
          })

          for (const col of selectedCols) {

            const colBody = {
              title: col.title,
              uniqueTitle: col.uniqueTitle,
              type: col.type
            }
            await this.$http.post(
              `${VUE_APP_PROXY_SERVER_URL}/spreadsheets/${this.importSpreadSheetUniqueTitle}/columns/`,
              colBody,
              { headers: { 'application-unique-title': this.$route.params.application } }
            )
          }

          const rowsToImport = this.importRows.map(row => {

            const obj = {}
            for (const col of selectedCols) {
              const idx = this.importColumns.findIndex(c => c.uniqueTitle === col.uniqueTitle)
              if (idx < 0) continue
              const raw = row[idx]
              if (col.type === 'number') {
                const n = parseFloat(raw)
                obj[col.uniqueTitle] = isNaN(n) ? null : n
              } else if (col.type === 'dateTime') {
                if (!isValidTimeZone(this.importTimeZone)) {
                  throw new Error('Import needs the time zone of the file for date and time columns.')
                }
                obj[col.uniqueTitle] = raw ? storeDateTime(raw, this.importTimeZone) : null
              } else {
                obj[col.uniqueTitle] = raw != null ? String(raw).trim() : ''
              }
            }
            return obj
          })

          const response = await this.$http.post(
            `${VUE_APP_PROXY_SERVER_URL}/data/${this.importSpreadSheetUniqueTitle}/bulk`,
            { rows: rowsToImport, reportRejected: true, acceptInvalid: this.importAcceptInvalid, timeZone: this.importTimeZone },
            { headers: { 'application-unique-title': this.$route.params.application } }
          )
          const rejected = response.data && Array.isArray(response.data.rejected) ? response.data.rejected : []

          this.importRejected = rejected
          await this.getSpreadSheets()
          const newSheet = this.spreadsheets.find(s => s.uniqueTitle === this.importSpreadSheetUniqueTitle)
          if (newSheet) this.loadSpreadSheet(newSheet)

          if (rejected.length && !this.importAcceptInvalid) {
            this.importSpreadSheetError = `${rejected.length} row${rejected.length === 1 ? '' : 's'} failed a validation rule and were not loaded.`
            return
          }

          this.importSpreadSheetModalVisible = false
          this.resetImportSpreadSheet()
        } catch (error) {

          console.error('Import failed:', error)
          const msg = error?.response?.data || error?.message || 'Import failed'
          this.importSpreadSheetError = typeof msg === 'string' ? msg : JSON.stringify(msg)
          event.preventDefault()
        }

        this.importSpreadSheetSubmitting = false
      },
      showDeleteSpreadSheet () {

        this.dismissCellEditors()
        this.deleteSpreadSheetModalVisible = true
      },
      showDeleteColumn () {

        this.dismissCellEditors()
        this.deleteColumnModalVisible = true
      },
      resetCreateColumnForm () {

        applyCreateColumnFormReset(this)
      },
      showCreateSpreadSheetColumn () {

        this.dismissCellEditors()
        this.resetCreateColumnForm()
        this.newColumnServerError = ''
        this.createColumnFormKey += 1
        this.createSpreadSheetColumnModalVisible = true
      },
      onNewRowFieldInput (fieldKey, value) {
        this.newRowData = { ...this.newRowData, [fieldKey]: value }
        if (this._newRowRelRefresh) clearTimeout(this._newRowRelRefresh)
        this._newRowRelRefresh = setTimeout(() => this.refreshNewRowRelationshipChoices(), 200)
      },
      async refreshNewRowRelationshipChoices () {
        for (const col of this.editableNewRowColumns || []) {
          if (col.type !== 'relationship') continue
          try {
            const response = await this.$http.get(
              `${VUE_APP_PROXY_SERVER_URL}/data/${this.uniqueTitle}/relationships/${col.uniqueTitle}/options`,
              {
                params: { forLink: 1, row: JSON.stringify(this.newRowData || {}) },
                headers: { 'application-unique-title': this.$route.params.application }
              }
            )
            const allowed = new Set(((response.data && response.data.options) || []).map(item => Number(item.id)))
            const current = this.newRowData[col.uniqueTitle] || []
            const next = current.filter(item => allowed.has(Number(item.id)))
            if (next.length !== current.length) {
              this.newRowData = { ...this.newRowData, [col.uniqueTitle]: next }
            }
          } catch (error) {
            console.error('Could not refresh relationship choices', error)
          }
        }
      },
      imageUploadName (value) {
        if (value && value.url) return value.url
        return value && value.filename ? value.filename : ''
      },
      onImageUrl (target, fieldKey, event) {
        const url = String(event.target.value || '').trim()
        if (!url) return
        if (target === 'new') this.onNewRowFieldInput(fieldKey, { url })
      },
      onNewDurationPart (fieldKey, part, event) {
        const current = this.newRowData[fieldKey] && typeof this.newRowData[fieldKey] === 'object' ? this.newRowData[fieldKey] : {}
        this.onNewRowFieldInput(fieldKey, { ...current, [part]: event.target.value })
      },
      newDurationSummary (column) {
        const value = this.newRowData[column.uniqueTitle]
        if (!value || !value.start || !value.end) return ''
        const amount = durationBetween(value.start, value.end, durationUnit(column))
        return amount == null ? '' : formatDuration(amount, durationUnit(column))
      },
      durationPart (item, fieldKey, part) {
        const draft = this._durationDraft && this._durationDraft[fieldKey]
        const value = draft && draft[part] != null ? draft[part] : item[`${fieldKey}_${part}`]
        if (!value) return ''
        return String(value).slice(0, 16)
      },
      onDurationPart (item, fieldKey, part, event) {
        if (!this._durationDraft) this._durationDraft = {}
        const current = this._durationDraft[fieldKey] || {
          start: item[`${fieldKey}_start`] ? String(item[`${fieldKey}_start`]).slice(0, 16) : '',
          end: item[`${fieldKey}_end`] ? String(item[`${fieldKey}_end`]).slice(0, 16) : ''
        }
        this._durationDraft[fieldKey] = { ...current, [part]: event.target.value }
      },
      saveDurationCell (item, fieldKey) {
        const draft = (this._durationDraft && this._durationDraft[fieldKey]) || {}
        this.updateCellAndClear({
          item,
          fieldKey,
          newValue: {
            start: draft.start || '',
            end: draft.end || ''
          }
        })
      },
      onSignatureDrawn (item, fieldKey, upload) {
        if (!upload) return
        this.updateCellAndClear({ item, fieldKey, newValue: upload })
      },
      readImageFile (file) {
        return new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = () => resolve(imageUploadFromDataUrl(reader.result, file.name, file.type))
          reader.onerror = () => reject(reader.error || new Error('Could not read that image'))
          reader.readAsDataURL(file)
        })
      },
      async onImageFieldFile (target, fieldKey, columnType, event) {
        const file = event.target.files && event.target.files[0]
        if (!file) return
        if (columnType !== 'file' && file.type && file.type.indexOf('image/') !== 0) {
          event.target.value = ''
          return
        }
        const upload = await this.readImageFile(file)
        if (target === 'bulk') this.onBulkValueInput(fieldKey, upload)
        else this.onNewRowFieldInput(fieldKey, upload)
      },
      onNewRowRelSearch (fieldKey, col, query) {
        const state = { ...(this.newRowRelState[fieldKey] || {}), query }
        this.newRowRelState = { ...this.newRowRelState, [fieldKey]: state }

        if (this._newRowRelSearchTimeout) clearTimeout(this._newRowRelSearchTimeout)
        this._newRowRelSearchTimeout = setTimeout(() => {
          this.doNewRowRelSearch(fieldKey, col)
        }, 250)
      },
      async doNewRowRelSearch (fieldKey, col) {
        const relatedSheet = col.relatedSpreadSheet
        if (!relatedSheet) return

        const state = { ...(this.newRowRelState[fieldKey] || {}), searching: true }
        this.newRowRelState = { ...this.newRowRelState, [fieldKey]: state }

        try {
          const response = await this.$http.get(
            `${VUE_APP_PROXY_SERVER_URL}/data/${this.uniqueTitle}/relationships/${col.uniqueTitle}/options`,
            {
              params: { q: state.query || '', forLink: 1, row: JSON.stringify(this.newRowData || {}) },
              headers: { 'application-unique-title': this.$route.params.application }
            }
          )
          const selectedIds = new Set((this.newRowData[fieldKey] || []).map(r => r.id))
          const results = ((response.data && response.data.options) || []).filter(r => !selectedIds.has(r.id))
          this.newRowRelState = {
            ...this.newRowRelState,
            [fieldKey]: { ...this.newRowRelState[fieldKey], results, searching: false }
          }
        } catch (error) {
          console.error('New row relationship search failed:', error)
          this.newRowRelState = {
            ...this.newRowRelState,
            [fieldKey]: { ...this.newRowRelState[fieldKey], results: [], searching: false }
          }
        }
      },
      addNewRowRelSelection (fieldKey, col, record) {
        const isSingleSelect = col.relationshipType === 'oneToOne' || col.relationshipType === 'manyToOne'
        const newRecord = { id: record.id, displayValue: record.displayValue }
        if (isSingleSelect) {
          this.newRowData = {
            ...this.newRowData,
            [fieldKey]: [newRecord]
          }
          this.newRowRelState = {
            ...this.newRowRelState,
            [fieldKey]: { query: '', results: [], searching: false }
          }
        } else {
          const current = this.newRowData[fieldKey] || []
          this.newRowData = {
            ...this.newRowData,
            [fieldKey]: [...current, newRecord]
          }
          this.doNewRowRelSearch(fieldKey, col)
        }
      },
      removeNewRowRelSelection (fieldKey, recordId) {
        const current = this.newRowData[fieldKey] || []
        this.newRowData = {
          ...this.newRowData,
          [fieldKey]: current.filter(r => r.id !== recordId)
        }
      },
      toggleRowSelection (id) {
        const idx = this.selectedRows.indexOf(id)
        if (idx >= 0) {
          this.selectedRows.splice(idx, 1)
        } else {
          this.selectedRows.push(id)
        }
      },
      toggleSelectAll () {
        if (this.allRowsSelected) {
          this.selectedRows = []
        } else {
          this.selectedRows = this.items.map(item => item.id)
        }
      },
      showDeleteRowsModal () {
        this.dismissCellEditors()
        this.deleteRowsModalVisible = true
      },
      showBulkActionsModal () {
        this.dismissCellEditors()
        this.bulkActionMode = ''
        this.bulkActionFilters = []
        this.bulkActionMatchMode = 'all'
        this.bulkActionMatchRequestId += 1
        this.applyBulkMatchPreview(this.items)
        this.bulkActionError = null
        this.bulkActionSuccess = null
        this.bulkActionSubmitting = false
        const data = {}
        for (const col of this.editableNewRowColumns) {
          data[col.uniqueTitle] = col.type === 'number' ? null : ''
        }
        this.bulkActionValues = data
        this.bulkFieldOps = {}
        this.bulkWorkflowUniqueTitle = ''
        this.bulkWorkflowMessage = ''
        this.bulkWorkflowError = false
        this.bulkAutomationUniqueTitle = ''
        this.bulkAutomationMessage = ''
        this.bulkAutomationError = false
        this.loadAvailableWorkflows()
        this.loadAvailableAutomations()
        this.bulkActionsModalVisible = true
      },
      showScheduledTasksModal () {
        this.dismissCellEditors()
        this.loadAvailableAutomations()
        this.loadAvailableWorkflows()
        this.scheduledTasksModalVisible = true
      },
      showWorkflowHooksModal () {
        this.dismissCellEditors()
        this.workflowHooksModalVisible = true
      },
      async loadAvailableAutomations () {
        try {
          const response = await this.$http.get(
            `${VUE_APP_PROXY_SERVER_URL}/automations/`,
            { headers: { 'application-unique-title': this.$route.params.application } }
          )
          this.availableAutomations = response.data || []
          this.preselectBulkAutomation()
        } catch (err) {
          console.warn('Failed to load automations:', err)
          this.availableAutomations = []
        }
      },
      async loadAvailableWorkflows () {
        try {
          const response = await this.$http.get(
            `${VUE_APP_PROXY_SERVER_URL}/step-workflows/`,
            { headers: { 'application-unique-title': this.$route.params.application } }
          )
          this.availableWorkflows = response.data || []
          this.preselectBulkWorkflow()
        } catch (err) {
          console.warn('Failed to load workflows:', err)
          this.availableWorkflows = []
        }
      },
      chooseBulkValues () {
        this.bulkActionFilters = []
        this.bulkActionMatchMode = 'all'
        this.bulkActionMatchRequestId += 1
        this.applyBulkMatchPreview(this.items)
        this.bulkActionError = null
        this.bulkActionSuccess = null
        this.bulkActionMode = 'values'
      },
      chooseBulkWorkflow () {
        this.bulkActionFilters = []
        this.bulkActionMatchMode = 'all'
        this.bulkActionMatchRequestId += 1
        this.bulkActionMatchCount = null
        this.bulkActionMatchRows = []
        this.bulkWorkflowMessage = ''
        this.bulkWorkflowError = false
        this.preselectBulkWorkflow()
        this.bulkActionMode = 'workflow'
      },
      preselectBulkWorkflow () {
        if (this.bulkWorkflowUniqueTitle) return
        if (this.sheetWorkflows.length === 1) this.bulkWorkflowUniqueTitle = this.sheetWorkflows[0].uniqueTitle
      },
      chooseBulkAutomation () {
        this.bulkActionFilters = []
        this.bulkActionMatchMode = 'all'
        this.bulkActionMatchRequestId += 1
        this.bulkActionMatchCount = null
        this.bulkActionMatchRows = []
        this.bulkAutomationMessage = ''
        this.bulkAutomationError = false
        this.preselectBulkAutomation()
        this.bulkActionMode = 'automation'
      },
      preselectBulkAutomation () {
        if (this.bulkAutomationUniqueTitle) return
        if (this.sheetAutomations.length === 1) this.bulkAutomationUniqueTitle = this.sheetAutomations[0].uniqueTitle
      },
      automationTypeLabel (type) {
        return automationTypeShort(type)
      },
      async bulkActionTargetRows () {
        if (this.selectedRows.length) {
          const ids = new Set(this.selectedRows.slice(0, 100).map((id) => String(id)))
          return (this.items || []).filter((item) => item && ids.has(String(item.id)))
        }
        if (this.bulkActionFilters.length) {
          const url = `${VUE_APP_PROXY_SERVER_URL}/data/${this.uniqueTitle}?userFilters=${encodeURIComponent(JSON.stringify(this.bulkActionFilters))}&userFilterMatchMode=${encodeURIComponent(this.bulkActionMatchMode)}`
          const response = await this.$http.get(url, { headers: { 'application-unique-title': this.$route.params.application } })
          return (Array.isArray(response.data) ? response.data : []).filter((row) => row && row.id != null).slice(0, 100)
        }
        return []
      },
      async bulkWorkflowRecordIds () {
        const rows = await this.bulkActionTargetRows()
        return rows.map((row) => row.id)
      },
      async runBulkWorkflow () {
        if (!this.canRunBulkWorkflow) return
        this.bulkWorkflowRunning = true
        this.bulkWorkflowMessage = ''
        this.bulkWorkflowError = false
        const workflow = this.sheetWorkflows.find((item) => item.uniqueTitle === this.bulkWorkflowUniqueTitle)
        const name = workflow ? workflow.title : this.bulkWorkflowUniqueTitle
        try {
          const ids = await this.bulkWorkflowRecordIds()
          let failed = 0
          for (const recordId of ids) {
            const response = await this.$http.post(`${VUE_APP_PROXY_SERVER_URL}/step-workflows/${this.bulkWorkflowUniqueTitle}/execute`, {
              recordId,
              trigger: 'bulk_action'
            }, {
              headers: { 'application-unique-title': this.$route.params.application }
            })
            if (!response.data || response.data.status === 'error') failed += 1
          }
          this.bulkWorkflowMessage = failed
            ? `${name} ran on ${ids.length} record${ids.length === 1 ? '' : 's'}. ${failed} failed.`
            : `${name} ran on ${ids.length} record${ids.length === 1 ? '' : 's'}.`
          this.bulkWorkflowError = failed > 0
          await this.loadSpreadSheetData()
        } catch (error) {
          const message = error && error.response && error.response.data
          this.bulkWorkflowMessage = typeof message === 'string' && message ? message : 'Could not run the workflow.'
          this.bulkWorkflowError = true
        } finally {
          this.bulkWorkflowRunning = false
        }
      },
      async runBulkAutomation () {
        if (!this.canRunBulkAutomation) return
        this.bulkAutomationRunning = true
        this.bulkAutomationMessage = ''
        this.bulkAutomationError = false
        const automation = this.sheetAutomations.find((item) => item.uniqueTitle === this.bulkAutomationUniqueTitle)
        const name = automation ? (automation.title || automation.uniqueTitle) : this.bulkAutomationUniqueTitle
        const columns = (this.spreadSheet && this.spreadSheet.columns) || []
        try {
          const rows = await this.bulkActionTargetRows()
          let failed = 0
          for (const row of rows) {
            const response = await this.$http.post(`${VUE_APP_PROXY_SERVER_URL}/automations/${this.bulkAutomationUniqueTitle}/execute`, {
              trigger: 'bulk_action',
              record: { id: row.id, spreadSheetUniqueTitle: this.uniqueTitle },
              variables: automationRecordVariables(automation, row, columns)
            }, {
              headers: { 'application-unique-title': this.$route.params.application }
            })
            if (!response.data || response.data.status === 'error') failed += 1
          }
          this.bulkAutomationMessage = failed
            ? `${name} ran on ${rows.length} record${rows.length === 1 ? '' : 's'}. ${failed} failed.`
            : `${name} ran on ${rows.length} record${rows.length === 1 ? '' : 's'}.`
          this.bulkAutomationError = failed > 0
          await this.loadSpreadSheetData()
        } catch (error) {
          const message = error && error.response && error.response.data
          this.bulkAutomationMessage = typeof message === 'string' && message ? message : 'Could not run the automation.'
          this.bulkAutomationError = true
        } finally {
          this.bulkAutomationRunning = false
        }
      },
      onBulkFilterChanged (filterEvent) {
        this.bulkActionFilters = filterEvent.filters || []
        this.bulkActionMatchMode = filterEvent.matchMode || 'all'
        this.bulkActionSuccess = null
        this.loadBulkMatchCount()
      },
      onBulkValueInput (fieldKey, value) {
        this.bulkActionValues = { ...this.bulkActionValues, [fieldKey]: value }
      },
      applyBulkMatchPreview (rows) {
        const preview = bulkMatchPreview(rows)
        this.bulkActionMatchCount = preview.count
        this.bulkActionMatchRows = preview.rows
      },
      async loadBulkMatchCount () {
        const requestId = this.bulkActionMatchRequestId + 1
        this.bulkActionMatchRequestId = requestId
        if (this.bulkActionFilters.length === 0) {
          if (this.bulkActionMode === 'workflow' || this.bulkActionMode === 'automation') {
            this.bulkActionMatchCount = null
            this.bulkActionMatchRows = []
            return
          }
          this.applyBulkMatchPreview(this.items)
          return
        }
        this.bulkActionMatchCount = null
        this.bulkActionMatchRows = []
        try {
          let url = `${VUE_APP_PROXY_SERVER_URL}/data/${this.uniqueTitle}`
          url += `?userFilters=${encodeURIComponent(JSON.stringify(this.bulkActionFilters))}&userFilterMatchMode=${encodeURIComponent(this.bulkActionMatchMode)}`
          const response = await this.$http.get(url, { headers: { 'application-unique-title': this.$route.params.application } })
          if (requestId !== this.bulkActionMatchRequestId) return
          this.applyBulkMatchPreview(Array.isArray(response.data) ? response.data : [])
        } catch (err) {
          if (requestId !== this.bulkActionMatchRequestId) return
          console.warn('Failed to count bulk matches:', err)
          this.bulkActionMatchCount = null
          this.bulkActionMatchRows = []
        }
      },
      bulkFieldOp (col) {

        if (this.bulkFieldOps[col.uniqueTitle]) return this.bulkFieldOps[col.uniqueTitle]

        return col.type === 'relationship' ? '' : 'replace'
      },
      bulkFieldOpOptions (col) {

        if (col.type === 'relationship') {

          return [
            { value: '', text: 'Leave the links unchanged' },
            { value: 'append', text: 'Append one record' },
            { value: 'remove', text: 'Remove one record' }
          ]
        }

        return [
          { value: 'replace', text: 'Replace the values' },
          { value: 'append', text: 'Append one value' },
          { value: 'remove', text: 'Remove one value' }
        ]
      },
      onBulkFieldOp (col, value) {

        this.bulkFieldOps = { ...this.bulkFieldOps, [col.uniqueTitle]: value }

        if (value === 'append' || value === 'remove') {

          this.bulkActionValues = { ...this.bulkActionValues, [col.uniqueTitle]: '' }
        }
      },
      bulkChangeList () {

        const changes = []

        for (const col of this.editableNewRowColumns) {

          const op = this.bulkFieldOp(col)

          if (op !== 'append' && op !== 'remove') continue

          const val = this.bulkActionValues[col.uniqueTitle]

          if (val === undefined || val === '' || val === null) continue

          changes.push({ column: col.uniqueTitle, op, value: val })
        }

        return changes
      },
      async executeBulkAction (event) {
        if (event && event.preventDefault) event.preventDefault()

        const values = {}
        const changes = this.bulkChangeList()
        for (const col of this.editableNewRowColumns) {
          if (col.type === 'relationship') continue
          if (this.bulkFieldOp(col) === 'append' || this.bulkFieldOp(col) === 'remove') continue
          const val = this.bulkActionValues[col.uniqueTitle]
          if (val !== undefined && val !== '' && val !== null) {
            values[col.uniqueTitle] = val
          }
        }

        if (Object.keys(values).length === 0 && !changes.length) return

        this.bulkActionSubmitting = true
        this.bulkActionError = null
        this.bulkActionSuccess = null

        try {
          const response = await this.$http.put(
            `${VUE_APP_PROXY_SERVER_URL}/data/${this.uniqueTitle}/bulk`,
            {
              filters: this.bulkActionFilters,
              matchMode: this.bulkActionMatchMode,
              values,
              changes
            },
            { headers: { 'application-unique-title': this.$route.params.application } }
          )
          const count = response.data.updated || 0
          this.bulkActionsModalVisible = false
          this.loadSpreadSheetData()
        } catch (error) {
          console.error('Bulk action failed:', error)
          const msg = error?.response?.data || error?.message || 'Bulk action failed'
          this.bulkActionError = typeof msg === 'string' ? msg : JSON.stringify(msg)
        }

        this.bulkActionSubmitting = false
      },
      async deleteSelectedRows () {
        if (!this.selectedRows.length) return

        this.deleteRowsSubmitting = true

        try {
          await this.$http.delete(`${VUE_APP_PROXY_SERVER_URL}/data/${this.uniqueTitle}`, {
            data: { ids: this.selectedRows },
            headers: { 'application-unique-title': this.$route.params.application }
          })
          this.selectedRows = []
          await this.loadSpreadSheetData()
        } catch (error) {
          console.error('Failed to delete rows:', error)
        }

        this.deleteRowsSubmitting = false
      },
      showNewRowModal () {

        this.dismissCellEditors()
        const data = {}
        const relState = {}
        for (const col of this.editableNewRowColumns) {
          if (col.type === 'relationship') {
            data[col.uniqueTitle] = []
            relState[col.uniqueTitle] = { query: '', results: [], searching: false }
          } else {
            data[col.uniqueTitle] = col.type === 'number' ? null : ''
          }
        }
        applyColumnDefaults(this.spreadSheet.columns, data)
        this.newRowError = null
        this.newRowData = data
        this.newRowRelState = relState
        this.newRowSubmitting = false
        this.newRowModalVisible = true
      },
      newRowFieldLabel (col) {

        const title = (col && (col.title || col.uniqueTitle)) || ''

        if (col && col.required && columnSupportsRequired(col)) return `${title} *`

        return title
      },
      async createNewRowFromModal (event) {

        if (event && event.preventDefault) event.preventDefault()

        if (!this.editableNewRowColumns.length) return

        const failures = recordFailures(this.editableNewRowColumns, this.newRowData)

        if (failures.length) {
          this.newRowError = failures[0].message
          return
        }

        this.newRowSubmitting = true
        this.newRowError = null

        const body = {}
        for (const col of this.editableNewRowColumns) {
          if (col.type === 'relationship') {
            const selections = this.newRowData[col.uniqueTitle] || []
            body[col.uniqueTitle] = selections.map(r => r.id)
          } else {
            const val = this.newRowData[col.uniqueTitle]
            if (val !== undefined && val !== '') {
              body[col.uniqueTitle] = val
            }
          }
        }

        try {
          await this.$http.post(
            `${VUE_APP_PROXY_SERVER_URL}/data/${this.uniqueTitle}`,
            body,
            { headers: { 'application-unique-title': this.$route.params.application } }
          )

          this.loadSpreadSheetData()
          this.newRowModalVisible = false
        } catch (error) {
          const data = error && error.response && error.response.data
          this.newRowError = typeof data === 'string' && data.trim() ? data : 'Could not add this row.'
          console.error('Failed to create new row:', error)
        }

        this.newRowSubmitting = false
      },
      async openReverseRelationshipSource (rev) {
        await this.loadSpreadSheet(rev.sourceSpreadsheet)
        this.showEditSpreadSheetColumn(rev.sourceColumn)
      },
      showEditSpreadSheetColumn (column) {

        this.dismissCellEditors()

        this.editColumnData = {
          title: column.title,
          uniqueTitle: column.uniqueTitle,
          type: column.type,
          relationshipType: column.relationshipType || ``,
          relatedSpreadSheet: column.relatedSpreadSheet || ``,
          formula: column.formula || ``,
          unique: !!column.unique,
          required: !!column.required,
          description: column.description || '',
          defaultValue: column.defaultValue == null ? '' : column.defaultValue,
          validationRules: column.validationRules || { rules: [] },
          valueRules: column.valueRules || { rules: [] },
          valueFormatting: column.valueFormatting || null,
          selectionMode: column.selectionMode || `multiple`,
          choices: Array.isArray(column.choices) ? column.choices.slice() : [],
          autoResize: !!column.autoResize,
          resizeWidth: column.resizeWidth || null,
          resizeHeight: column.resizeHeight || null,
          keepProportions: !!column.keepProportions,
          dateOnly: !!column.dateOnly
        }

        this.editSpreadSheetUniqueColumnTitlePriorToEdit = String(column.uniqueTitle)
        this.editSpreadSheetUniqueColumnTitle = column.uniqueTitle
        this.editColumnServerError = ''

        this.editSpreadSheetColumnModalVisible = true
      },
      async createSpreadSheet (event) {

        this.newSpreadSheetSubmitAttempted = true

        if (!String(this.newSpreadSheetTitle).trim().length || !String(this.newSpreadSheetUniqueTitle).trim().length || this.newSpreadSheetUniqueTitleCollision) {

          return event.preventDefault()
        }

        const body = {
          title: this.newSpreadSheetTitle,
          uniqueTitle: this.newSpreadSheetUniqueTitle
        }

        try {

          await this.$http.post(`${VUE_APP_PROXY_SERVER_URL}/spreadsheets/`, body, {
            headers: {
              'Content-Type': 'application/json',
              'application-unique-title': this.$route.params.application
            }
          })
        } catch (error) {

          console.error(`error creating spreadsheet:`, error)
        }

        this.newSpreadSheetTitle = ``
        this.newSpreadSheetUniqueTitle = ``

        this.getSpreadSheets()
      },
      async deleteSpreadSheet (event) {

        if (this.deleteSpreadSheetTitle !== this.title) {

          event.preventDefault()

          return
        }

        this.deleteSpreadSheetModalVisible = false

        try {

          await this.$http.delete(`${VUE_APP_PROXY_SERVER_URL}/spreadsheets/${this.uniqueTitle}`, {headers: {'application-unique-title': this.$route.params.application}})
        } catch (error) {

          console.error(`error deleting spreadsheet:`, error)
        }

        this.deleteSpreadSheetTitle = ``

        this.getSpreadSheets()
      },
      async deleteColumn (event) {

        if (this.deleteColumnTitle !== this.editSpreadSheetUniqueColumnTitle) {

          event.preventDefault()

          return
        }

        this.deleteColumnModalVisible = false

        try {

          await this.$http.delete(`${VUE_APP_PROXY_SERVER_URL}/spreadsheets/${this.uniqueTitle}/columns/${this.deleteColumnTitle}`, {headers: {'application-unique-title': this.$route.params.application}})
        } catch (error) {

          console.error(`error deleting column:`, error)
        }

        this.deleteColumnTitle = ``

        this.getSpreadSheets()
      },
      requestErrorMessage (error) {

        const data = error && error.response && error.response.data

        if (typeof data === 'string' && data.trim()) return data.trim()
        if (data && typeof data.error === 'string' && data.error.trim()) return data.error.trim()

        return 'The column could not be saved.'
      },
      async createSpreadSheetColumn (event) {

        this.newColumnSubmitAttempted = true
        this.newColumnServerError = ''

        if (!String(this.newSpreadSheetColumnTitle).trim().length || !String(this.newSpreadSheetUniqueColumnTitle).trim().length || !this.newSpreadSheetUniqueColumnType || this.newColumnUniqueTitleCollision) {

          return event.preventDefault()
        }

        event.preventDefault()

        const body = {
          title: this.newSpreadSheetColumnTitle,
          uniqueTitle: this.newSpreadSheetUniqueColumnTitle,
          type: this.newSpreadSheetUniqueColumnType,
          dateOnly: this.newSpreadSheetUniqueColumnType === 'dateTime' ? !!this.newSpreadSheetColumnDateOnly : false
        }

        if (this.newSpreadSheetUniqueColumnType === `multiselect`) {

          body.selectionMode = this.newSpreadSheetColumnSelectionMode || `multiple`
          body.choices = this.newSpreadSheetColumnChoices.map(choice => String(choice || ``).trim()).filter(Boolean)
        }

        if (this.newSpreadSheetUniqueColumnType === `image`) {

          body.autoResize = !!this.newSpreadSheetColumnAutoResize
          body.resizeWidth = this.newSpreadSheetColumnResizeWidth
          body.resizeHeight = this.newSpreadSheetColumnResizeHeight
          body.keepProportions = !!this.newSpreadSheetColumnKeepProportions
        }

        if (this.newSpreadSheetUniqueColumnType === `duration`) {

          body.durationUnit = this.newSpreadSheetColumnDurationUnit || `hours`
        }

        if (this.newSpreadSheetUniqueColumnType === `sequence`) {

          body.sequenceStart = this.newSpreadSheetColumnSequenceStart
        }

        if (this.newSpreadSheetUniqueColumnType === `relationship`) {

          body.newSpreadSheetUniqueColumnRelationshipType = this.newSpreadSheetUniqueColumnRelationshipType
          body.newSpreadSheetUniqueColumnRelationedSpreadSheet = this.newSpreadSheetUniqueColumnRelationedSpreadSheet
          body.displayFormula = this.newRelationshipSettings.displayFormula || ''
          body.cascadeFilters = this.newRelationshipSettings.cascadeFilters || []
          body.cascadeMatchMode = this.newRelationshipSettings.cascadeMatchMode || 'all'
          body.showAllUntilParent = !!this.newRelationshipSettings.showAllUntilParent
          body.allowAdd = !!this.newRelationshipSettings.allowAdd
          body.addFields = this.newRelationshipSettings.addFields || []
        }

        let updatedSpreadSheet

        try {

          updatedSpreadSheet = await this.$http.post(`${VUE_APP_PROXY_SERVER_URL}/spreadsheets/${this.uniqueTitle}/columns/`, body, {headers:{'application-unique-title': this.$route.params.application}})
        } catch (error) {

          console.error(`error creating column:`, error)
          this.newColumnServerError = this.requestErrorMessage(error)
          return
        }

        this.createSpreadSheetColumnModalVisible = false
        this.resetCreateColumnForm()

        await this.getSpreadSheets()
        const spreadsheetToLoad = updatedSpreadSheet?.data ?? this.spreadsheets.find(s => s.uniqueTitle === this.uniqueTitle) ?? this.spreadsheets[0]
        if (spreadsheetToLoad) {
          this.loadSpreadSheet(spreadsheetToLoad)
        }
      },
      onColumnsUpdate (newColumns, spreadsheet) {
        spreadsheet.columns = newColumns
      },
      moveSpreadsheetByKey (spreadsheet, direction) {

        const index = this.spreadsheets.findIndex(item => item.uniqueTitle === spreadsheet.uniqueTitle)

        if (!moveListItem(this.spreadsheets, index, direction)) return

        this.onSpreadsheetDragEnd()
        this.focusReorder(spreadsheet.uniqueTitle)
      },
      moveColumnByKey (spreadsheet, column, direction) {

        const index = (spreadsheet.columns || []).findIndex(item => item.uniqueTitle === column.uniqueTitle)

        if (!moveListItem(spreadsheet.columns, index, direction)) return

        this.onColumnDragEnd(spreadsheet)
        this.focusReorder(column.uniqueTitle)
      },
      focusReorder (id) {

        this.$nextTick(() => {

          const handle = this.$el && this.$el.querySelector(`[data-reorder-id="${CSS.escape(id)}"]`)

          if (handle) handle.focus()
        })
      },
      async onSpreadsheetDragEnd () {

        const orderedUniqueTitles = this.spreadsheets.map(s => s.uniqueTitle)

        try {
          await this.$http.put(`${VUE_APP_PROXY_SERVER_URL}/spreadsheets/reorder`, {
            orderedUniqueTitles
          }, {
            headers: { 'application-unique-title': this.$route.params.application }
          })
        } catch (error) {
          console.error('Failed to reorder spreadsheets:', error)
          this.getSpreadSheets()
        }
      },
      async onColumnDragEnd (spreadsheet) {

        const orderedColumnUniqueTitles = spreadsheet.columns.map(c => c.uniqueTitle)

        try {
          const result = await this.$http.put(
            `${VUE_APP_PROXY_SERVER_URL}/spreadsheets/${spreadsheet.uniqueTitle}/columns/reorder`,
            { orderedColumnUniqueTitles },
            { headers: { 'application-unique-title': this.$route.params.application } }
          )

          if (result.data) {
            const idx = this.spreadsheets.findIndex(s => s.uniqueTitle === spreadsheet.uniqueTitle)
            if (idx >= 0) {
              this.spreadsheets[idx] = result.data
            }
            if (this.spreadSheet && this.spreadSheet.uniqueTitle === spreadsheet.uniqueTitle) {
              this.spreadSheet = result.data
            }
          }
        } catch (error) {
          console.error('Failed to reorder columns:', error)
          this.getSpreadSheets()
        }
      },
      showSpreadSheetHistory () {
        this.dismissCellEditors()
        this.historyRecordId = null
        this.historyModalVisible = true
      },
      async resetAuthenticator (recordId) {

        const ok = window.confirm('Clear this person’s authenticator and recovery codes? They will set one up the next time they sign in.')

        if (!ok) return

        try {

          await this.$http.post(
            `${VUE_APP_PROXY_SERVER_URL}/data/${this.uniqueTitle}/${recordId}/authenticator/reset`,
            {},
            { headers: { 'application-unique-title': this.$route.params.application } }
          )
          window.alert('Authenticator cleared. They will set one up the next time they sign in.')
        } catch (error) {

          const data = error && error.response && error.response.data
          const message = data && data.error ? data.error : 'Could not reset the authenticator.'

          window.alert(message)
        }
      },
      showRecordHistory (recordId) {
        this.dismissCellEditors()
        this.historyRecordId = recordId
        this.historyModalVisible = true
      },
      async editSpreadSheetColumn (columnData) {

        this.editColumnServerError = ''

        const body = {
          title: columnData.title,
          uniqueTitle: columnData.uniqueTitle,
          type: columnData.type,
          unique: !!columnData.unique,
          required: !!columnData.required,
          description: columnData.description || '',
          defaultValue: columnData.defaultValue == null ? '' : columnData.defaultValue,
          validationRules: columnData.validationRules || { rules: [] },
          valueRules: columnData.valueRules || { rules: [] },
          valueFormatting: columnData.valueFormatting || null,
          dateOnly: columnData.type === 'dateTime' ? !!columnData.dateOnly : false
        }

        if (columnData.type === `relationship`) {

          body.newSpreadSheetUniqueColumnRelationshipType = columnData.relationshipType
          body.newSpreadSheetUniqueColumnRelationedSpreadSheet = columnData.relatedSpreadSheet
          body.displayFormula = columnData.displayFormula || ''
          body.cascadeFilters = columnData.cascadeFilters || []
          body.cascadeMatchMode = columnData.cascadeMatchMode || 'all'
          body.showAllUntilParent = !!columnData.showAllUntilParent
          body.allowAdd = !!columnData.allowAdd
          body.addFields = columnData.addFields || []
        }

        if (columnData.type === `formula`) {

          body.formula = columnData.formula
        }

        if (columnData.type === `multiselect`) {

          body.selectionMode = columnData.selectionMode || `multiple`
          body.choices = Array.isArray(columnData.choices) ? columnData.choices : []
        }

        if (columnData.type === `image`) {

          body.autoResize = !!columnData.autoResize
          body.resizeWidth = columnData.resizeWidth
          body.resizeHeight = columnData.resizeHeight
          body.keepProportions = !!columnData.keepProportions
        }

        if (columnData.type === `duration`) body.durationUnit = columnData.durationUnit || `hours`
        if (columnData.type === `sequence`) body.sequenceStart = columnData.sequenceStart

        let updatedSpreadSheet

        try {

          updatedSpreadSheet = await this.$http.put(
            `${VUE_APP_PROXY_SERVER_URL}/spreadsheets/${this.uniqueTitle}/columns/${this.editSpreadSheetUniqueColumnTitlePriorToEdit}`,
            body,
            {
              headers: {
                'application-unique-title': this.$route.params.application
              }
            }
          )
        } catch (error) {

          console.error(`error updating column:`, error)
          this.editColumnServerError = this.requestErrorMessage(error)
          return
        }

        this.editSpreadSheetColumnModalVisible = false
        this.editColumnData = {}
        this.editSpreadSheetUniqueColumnTitle = ``

        await this.getSpreadSheets()
        const spreadsheetToLoad = updatedSpreadSheet?.data ?? this.spreadsheets.find(s => s.uniqueTitle === this.uniqueTitle) ?? this.spreadsheets[0]
        if (spreadsheetToLoad) {
          this.loadSpreadSheet(spreadsheetToLoad)
        }
      }
    },
    computed: {
      sheetWorkflows () {
        return (this.availableWorkflows || []).filter((workflow) => {
          return workflow && workflow.spreadSheetUniqueTitle === this.uniqueTitle
        })
      },
      sheetAutomations () {
        const columns = (this.spreadSheet && this.spreadSheet.columns) || []
        return (this.availableAutomations || []).reduce((list, automation) => {
          const use = automation && automationSheetUse(automation, this.uniqueTitle, columns)
          if (use) list.push(Object.assign({}, automation, { use }))
          return list
        }, [])
      },
      bulkActionTitle () {
        if (this.bulkActionMode === 'values') return 'bulk actions — update values'
        if (this.bulkActionMode === 'workflow') return 'bulk actions — run a step workflow'
        if (this.bulkActionMode === 'automation') return 'bulk actions — run an automation'
        return 'bulk actions'
      },
      selectedBulkPreviewRows () {
        const ids = new Set(this.selectedRows.slice(0, 100).map((id) => String(id)))
        return (this.items || []).filter((item) => item && ids.has(String(item.id)))
      },
      bulkWorkflowTargetCount () {
        if (this.selectedRows.length) return Math.min(this.selectedRows.length, 100)
        if (this.bulkActionFilters.length && this.bulkActionMatchCount != null) return Math.min(this.bulkActionMatchCount, 100)
        return 0
      },
      canRunBulkWorkflow () {
        return !!this.bulkWorkflowUniqueTitle && !this.bulkWorkflowRunning && this.bulkWorkflowTargetCount > 0
      },
      canRunBulkAutomation () {
        return !!this.bulkAutomationUniqueTitle && !this.bulkAutomationRunning && this.bulkWorkflowTargetCount > 0
      },
      bulkWorkflowTargetText () {
        const noun = this.bulkActionMode === 'automation' ? 'automation' : 'workflow'
        if (this.selectedRows.length) {
          const count = Math.min(this.selectedRows.length, 100)
          const extra = this.selectedRows.length > 100 ? ` The first 100 of ${this.selectedRows.length} selected records are included.` : ''
          return `Runs on ${count} selected record${count === 1 ? '' : 's'}.${extra}`
        }
        if (this.bulkActionFilters.length && this.bulkActionMatchCount != null) {
          const count = Math.min(this.bulkActionMatchCount, 100)
          const extra = this.bulkActionMatchCount > 100 ? ` The first 100 of ${this.bulkActionMatchCount} matching records are included.` : ''
          return `Runs on ${count} record${count === 1 ? '' : 's'} that match the filter.${extra}`
        }
        return `Check one or more rows, or set a filter, to choose which records run the ${noun}.`
      },
      containerStyle () {
        return { height: `${this.containerHeight}px` }
      },
      openLoginPages () {

        return this.loginSpreadSheets[this.uniqueTitle] || []
      },
      openLoginNote () {

        return loginSpreadSheetNote(this.openLoginPages)
      },
      aiColumnsSpreadSheetLabel () {
        const t = this.title || this.uniqueTitle || `selected spreadsheet`
        return `describe the columns to add to "${t}"`
      },
      newRelatedSheetColumns () {
        const sheet = (this.spreadsheets || []).find((item) => item.uniqueTitle === this.newSpreadSheetUniqueColumnRelationedSpreadSheet)
        return (sheet && sheet.columns) || []
      },
      newColumnRelationshipTypes () {
        const currentName = this.title || 'current spreadsheet'
        const relatedUniqueTitle = this.newSpreadSheetUniqueColumnRelationedSpreadSheet
        const relatedOption = relatedUniqueTitle
          ? this.spreadSheetOptions.find(o => o.value === relatedUniqueTitle)
          : null
        const relatedName = relatedOption ? relatedOption.text : 'related spreadsheet'
        return [
          { value: 'oneToOne', text: `one to one - one ${currentName} to one ${relatedName}` },
          { value: 'oneToMany', text: `one to many - one ${currentName} to many ${relatedName}` },
          { value: 'manyToOne', text: `many to one - many ${currentName} to one ${relatedName}` },
          { value: 'manyToMany', text: `many to many - many ${currentName} to many ${relatedName}` }
        ]
      },
      spreadsheetWrapperStyle () {
        return { maxHeight: `${Math.max(200, this.containerHeight - 180)}px` }
      },
      newSpreadSheetTitleState () {
        if (this.newSpreadSheetSubmitAttempted && !String(this.newSpreadSheetTitle).trim().length) return false
        return null
      },
      newSpreadSheetTitleFeedback () {
        if (this.newSpreadSheetSubmitAttempted && !String(this.newSpreadSheetTitle).trim().length) return 'a spreadsheet title is required'
        return ''
      },
      newSpreadSheetUniqueTitleCollision () {
        if (!this.newSpreadSheetUniqueTitle || !this.spreadsheets) return false
        return this.spreadsheets.some(s => s.uniqueTitle === this.newSpreadSheetUniqueTitle)
      },
      newSpreadSheetUniqueTitleState () {
        if (this.newSpreadSheetUniqueTitleCollision) return false
        if (this.newSpreadSheetSubmitAttempted && !String(this.newSpreadSheetUniqueTitle).trim().length) return false
        return null
      },
      newSpreadSheetUniqueTitleFeedback () {
        if (this.newSpreadSheetUniqueTitleCollision) return 'this unique title is already in use by another spreadsheet'
        if (this.newSpreadSheetSubmitAttempted && !String(this.newSpreadSheetUniqueTitle).trim().length) return 'a unique title is required'
        return ''
      },
      newColumnTitleState () {
        if (this.newColumnSubmitAttempted && !String(this.newSpreadSheetColumnTitle).trim().length) return false
        return null
      },
      newColumnTitleFeedback () {
        if (this.newColumnSubmitAttempted && !String(this.newSpreadSheetColumnTitle).trim().length) return 'a column title is required'
        return ''
      },
      newColumnTypeState () {
        if (this.newColumnSubmitAttempted && !this.newSpreadSheetUniqueColumnType) return false
        return null
      },
      newColumnTypeFeedback () {
        if (this.newColumnSubmitAttempted && !this.newSpreadSheetUniqueColumnType) return 'a column type is required'
        return ''
      },
      newColumnSameSheetCollision () {
        if (!this.newSpreadSheetUniqueColumnTitle || !this.spreadSheet || !this.spreadSheet.columns) return false
        return this.spreadSheet.columns.some(c => c.uniqueTitle === this.newSpreadSheetUniqueColumnTitle)
      },
      newColumnRelationshipConflict () {
        if (this.newSpreadSheetUniqueColumnType !== 'relationship' || this.newColumnSameSheetCollision) return null
        return relationshipUniqueTitleConflict(this.spreadsheets, this.newSpreadSheetUniqueColumnTitle)
      },
      newColumnUniqueTitleCollision () {
        return this.newColumnSameSheetCollision || !!this.newColumnRelationshipConflict
      },
      newColumnUniqueTitleEmpty () {
        return !String(this.newSpreadSheetUniqueColumnTitle).trim().length
      },
      newColumnUniqueTitleState () {
        if (this.newColumnUniqueTitleCollision) return false
        if (this.newColumnSubmitAttempted && this.newColumnUniqueTitleEmpty) return false
        return null
      },
      newColumnUniqueTitleFeedback () {
        if (this.newColumnRelationshipConflict) return relationshipUniqueTitleConflictMessage(this.newColumnRelationshipConflict)
        if (this.newColumnSameSheetCollision) return 'this unique title is already in use on this spreadsheet'
        if (this.newColumnSubmitAttempted && this.newColumnUniqueTitleEmpty) return 'a unique title is required'
        return ''
      },
      currentSpreadsheetColumns () {
        if (!this.spreadSheet || !this.spreadSheet.columns) return []
        return this.spreadSheet.columns
      },
      reverseRelationshipColumns () {
        if (!this.spreadSheet || !this.spreadsheets) return []
        const currentUniqueTitle = this.spreadSheet.uniqueTitle
        const inverseTypeMap = {
          oneToOne: 'oneToOne',
          oneToMany: 'manyToOne',
          manyToOne: 'oneToMany',
          manyToMany: 'manyToMany'
        }
        const result = []
        for (const ss of this.spreadsheets) {
          if (!ss || ss.uniqueTitle === currentUniqueTitle || !ss.columns) continue
          for (const col of ss.columns) {
            if (col.type !== 'relationship' || col.relatedSpreadSheet !== currentUniqueTitle) continue
            result.push({
              uniqueTitle: `_reverse_${ss.uniqueTitle}_${col.uniqueTitle}`,
              label: `${ss.title || ss.uniqueTitle} (via ${col.title || col.uniqueTitle})`,
              inverseRelationshipType: inverseTypeMap[col.relationshipType] || col.relationshipType || 'relationship',
              sourceSpreadsheet: ss,
              sourceColumn: col
            })
          }
        }
        return result
      },
      selectedRowSet () {
        return new Set(this.selectedRows)
      },
      columnMetaMap () {
        if (!this.spreadSheet || !this.spreadSheet.columns) return {}
        const map = {}
        for (const col of this.spreadSheet.columns) {
          map[col.uniqueTitle] = col
        }
        return map
      },
      virtualStartIndex () {
        return Math.max(0, Math.floor(this.virtualScrollTop / this.virtualRowHeight) - this.virtualBufferRows)
      },
      virtualEndIndex () {
        const viewportHeight = this.containerHeight - 180
        return Math.min(
          this.items.length,
          Math.ceil((this.virtualScrollTop + viewportHeight) / this.virtualRowHeight) + this.virtualBufferRows
        )
      },
      virtualItems () {
        return this.items.slice(this.virtualStartIndex, this.virtualEndIndex)
      },
      allRowsSelected () {
        return this.items.length > 0 && this.selectedRows.length === this.items.length
      },
      someRowsSelected () {
        return this.selectedRows.length > 0
      },
      tableFields () {
        if (!this.spreadSheet || !this.spreadSheet.columns) return []
        const fields = [
          { key: 'id', label: 'ID', type: 'number', editable: false }
        ]
        for (const col of this.spreadSheet.columns) {
          if (col.type === 'relationship' || col.type === 'formula' || col.type === 'sequence') {
            fields.push({ key: col.uniqueTitle, label: col.title, type: col.type, editable: false, durationUnit: col.durationUnit, dateOnly: !!col.dateOnly, valueFormatting: col.valueFormatting || null })
          } else {
            fields.push({ key: col.uniqueTitle, label: col.title, type: col.type || 'text', editable: true, durationUnit: col.durationUnit, dateOnly: !!col.dateOnly, valueFormatting: col.valueFormatting || null })
          }
        }
        fields.push({ key: 'createdAt', label: 'Created At', type: 'dateTime', editable: false })
        fields.push({ key: 'updatedAt', label: 'Updated At', type: 'dateTime', editable: false })
        return fields
      },
      fieldMap () {
        const map = {}
        for (const f of this.tableFields) {
          map[f.key] = f
        }
        return map
      },
      editableNewRowColumns () {
        if (!this.spreadSheet || !this.spreadSheet.columns) return []
        return this.spreadSheet.columns.filter(
          col => col.type !== 'formula' && col.type !== 'sequence'
        )
      },
      importSpreadSheetReady () {
        if (!this.importCsvParsed || !this.importColumns.length) return false
        const hasSelected = this.importColumns.some(c => c.selected)
        const titleOk = String(this.importSpreadSheetTitle).trim().length > 0
        const uniqueOk = String(this.importSpreadSheetUniqueTitle).trim().length > 0
        const noCollision = !this.spreadsheets.some(s => s.uniqueTitle === this.importSpreadSheetUniqueTitle)
        const zoneOk = !this.importNeedsTimeZone || isValidTimeZone(this.importTimeZone)
        return hasSelected && titleOk && uniqueOk && noCollision && zoneOk
      },
      importNeedsTimeZone () {
        return this.importColumns.some(col => col.selected && col.type === 'dateTime')
      },
      importTimeZoneOptions () {
        return [{ value: '', text: 'Choose the file time zone' }].concat(
          timeZoneChoices().map(zone => ({ value: zone, text: timeZoneLabel(zone) }))
        )
      },
      viewerTimeZone () {
        return browserTimeZone()
      },
      importSelectedColumnsCount () {
        return this.importColumns.filter(c => c.selected).length
      },
      importRowCount () {
        return this.importRows.length
      },
      importSpreadSheetTitleState () {
        if (!String(this.importSpreadSheetTitle).trim().length) return false
        return null
      },
      importSpreadSheetTitleFeedback () {
        if (!String(this.importSpreadSheetTitle).trim().length) return 'a spreadsheet title is required'
        return ''
      },
      importSpreadSheetUniqueTitleState () {
        if (this.spreadsheets.some(s => s.uniqueTitle === this.importSpreadSheetUniqueTitle)) return false
        if (!String(this.importSpreadSheetUniqueTitle).trim().length) return false
        return null
      },
      importSpreadSheetUniqueTitleFeedback () {
        if (this.spreadsheets.some(s => s.uniqueTitle === this.importSpreadSheetUniqueTitle)) return 'this unique title is already in use'
        if (!String(this.importSpreadSheetUniqueTitle).trim().length) return 'a unique title is required'
        return ''
      },
      copySpreadSheetUniqueTitleCollision () {
        if (!this.copySpreadSheetUniqueTitle || !this.spreadsheets) return false
        return this.spreadsheets.some(s => s.uniqueTitle === this.copySpreadSheetUniqueTitle)
      },
      copySpreadSheetTitleState () {
        if (this.copySpreadSheetSubmitAttempted && !String(this.copySpreadSheetTitle).trim().length) return false
        return null
      },
      copySpreadSheetTitleFeedback () {
        if (this.copySpreadSheetSubmitAttempted && !String(this.copySpreadSheetTitle).trim().length) return 'a spreadsheet title is required'
        return ''
      },
      copySpreadSheetUniqueTitleState () {
        if (this.copySpreadSheetUniqueTitleCollision) return false
        if (this.copySpreadSheetSubmitAttempted && !String(this.copySpreadSheetUniqueTitle).trim().length) return false
        return null
      },
      copySpreadSheetUniqueTitleFeedback () {
        if (this.copySpreadSheetUniqueTitleCollision) return 'this unique title is already in use by another spreadsheet'
        if (this.copySpreadSheetSubmitAttempted && !String(this.copySpreadSheetUniqueTitle).trim().length) return 'a unique title is required'
        return ''
      },
      bulkActionHasValues () {
        if (!this.editableNewRowColumns.length) return false
        if (this.bulkChangeList().length) return true
        return this.editableNewRowColumns.some(col => {
          if (col.type === 'relationship') return false
          if (this.bulkFieldOp(col) === 'append' || this.bulkFieldOp(col) === 'remove') return false
          const val = this.bulkActionValues[col.uniqueTitle]
          return val !== undefined && val !== '' && val !== null
        })
      },
      activeFilterCount () {
        return this.currentFilters.length
      },
      filterFieldGroups () {
        if (!this.spreadSheet || !this.spreadSheet.columns) return []

        const TYPE_MAP = {
          text: 'string',
          password: 'string',
          number: 'number',
          dateTime: 'datetime',
          formula: 'string',
          multiselect: 'string',
          richText: 'string',
          address: 'string',
          duration: 'number',
          sequence: 'number',
          signature: 'signature'
        }

        const buildFields = (columns, includeBuiltins, includeRelationships) => {
          const fields = []
          if (includeBuiltins) {
            fields.push({ field: 'id', displayName: 'ID', dataType: 'number' })
          }
          if (columns) {
            for (const col of columns) {
              if (col.type === 'relationship') {
                if (includeRelationships) {
                  fields.push({
                    field: col.uniqueTitle,
                    displayName: col.title || col.uniqueTitle,
                    dataType: 'relationship',
                    relatedSpreadSheet: col.relatedSpreadSheet,
                    relationshipType: col.relationshipType
                  })
                }
                continue
              }
              const dt = TYPE_MAP[col.type]
              if (!dt) continue
              fields.push({ field: col.uniqueTitle, displayName: col.title || col.uniqueTitle, dataType: dt })
            }
          }
          if (includeBuiltins) {
            fields.push({ field: 'createdAt', displayName: 'Created At', dataType: 'datetime' })
            fields.push({ field: 'updatedAt', displayName: 'Updated At', dataType: 'datetime' })
          }
          return fields
        }

        const groups = []
        const currentColumns = this.spreadSheet.columns || []
        const ssUniqueTitle = this.spreadSheet.uniqueTitle

        // Direct fields
        const directFields = buildFields(currentColumns, true, true)
        if (directFields.length > 0) {
          groups.push({
            key: 'direct',
            label: this.spreadSheet.title || ssUniqueTitle,
            fields: directFields.map(f => ({
              ...f,
              key: `direct::${f.field}`,
              source: 'direct',
              relationshipColumn: null,
              relatedSpreadsheet: null,
              sourceSpreadsheet: null,
              sourceRelationshipColumn: null
            }))
          })
        }

        // Forward relationship groups
        const relColumns = currentColumns.filter(c => c.type === 'relationship')
        for (const relCol of relColumns) {
          const relSS = this.spreadsheets.find(s => s.uniqueTitle === relCol.relatedSpreadSheet)
          if (!relSS) continue
          const relFields = buildFields(relSS.columns, true, false)
          groups.push({
            key: `forward::${relCol.uniqueTitle}`,
            label: `→ ${relSS.title || relSS.uniqueTitle} (via ${relCol.title || relCol.uniqueTitle})`,
            fields: relFields.map(f => ({
              ...f,
              key: `forward::${relCol.uniqueTitle}::${f.field}`,
              source: 'forward',
              relationshipColumn: relCol.uniqueTitle,
              relatedSpreadsheet: relSS.uniqueTitle,
              sourceSpreadsheet: null,
              sourceRelationshipColumn: null
            }))
          })
        }

        // Reverse relationship groups
        for (const ss of this.spreadsheets) {
          if (ss.uniqueTitle === ssUniqueTitle) continue
          if (!ss.columns) continue
          for (const col of ss.columns) {
            if (col.type === 'relationship' && col.relatedSpreadSheet === ssUniqueTitle) {
              const revFields = buildFields(ss.columns, true, false)
              groups.push({
                key: `reverse::${ss.uniqueTitle}::${col.uniqueTitle}`,
                label: `← ${ss.title || ss.uniqueTitle} (their ${col.title || col.uniqueTitle} → here)`,
                fields: revFields.map(f => ({
                  ...f,
                  key: `reverse::${ss.uniqueTitle}::${col.uniqueTitle}::${f.field}`,
                  source: 'reverse',
                  relationshipColumn: null,
                  relatedSpreadsheet: null,
                  sourceSpreadsheet: ss.uniqueTitle,
                  sourceRelationshipColumn: col.uniqueTitle
                }))
              })
            }
          }
        }

        return groups
      }
    },
    components: {
      FancyGridVue,
      EditableCell,
      MultiselectChoices,
      ImageCell,
      SignaturePad,
      RelationshipEditor,
      RelationshipColumnSettings,
      EditColumnModal,
      RuntimeFilterBuilder,
      RichTextCell,
      RecordHistory,
      ScheduledTasks,
      SpreadsheetWorkflowHooks,
      draggable,
      DragHandle,
      AiTokenUsage,
      ImportRows
    }
  }
</script>

<style scoped>

#context-nav {
  background-color: #181915 !important;
  border-bottom: 1px #474842 solid;
}

.datatable-container {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background-color: #1e1e1b;
}

.datatable-nav-row {
  flex-shrink: 0;
}

.datatable-main-row {
  flex: 1 1 0;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-wrap: nowrap;
  align-items: stretch;
  background-color: #1e1e1b;
}

.datatable-sidebar-col {
  height: 100%;
  min-height: 0;
  overflow: hidden;
  flex-shrink: 0;
}

#spreadsheet-list-col {
  padding: 0 !important;
  flex: 0 0 280px;
  width: 280px;
  max-width: 280px;
}

.datatable-main-row .spreadsheet-content-col {
  flex: 1 1 0;
  width: auto;
  max-width: none;
  min-width: 0;
}

.sidebar {
  background-color: #141412;
  border-right: 1px solid #2e2e2b;
  height: 100%;
  overflow-y: auto;
  padding-bottom: 32px;
}

.sidebar-section-label {
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #a8a8a4;
  padding: 14px 14px 8px;
}

.sidebar-group {
  margin-bottom: 1px;
}

.sidebar-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 14px;
  cursor: pointer;
  color: #b0b0ae;
  font-size: 0.875rem;
  border-radius: 0;
  transition: background 0.1s, color 0.1s;
  user-select: none;
}
.sidebar-item:hover {
  background-color: #1e1e1b;
  color: #e0e0de;
}
.sidebar-item.active {
  background-color: #1a1a17;
  color: #e8e8e6;
  border-left: 2px solid #9ad12b;
  padding-left: 12px;
}

.sidebar-icon {
  font-size: 0.75rem;
  color: #8a8a86;
  flex-shrink: 0;
}
.sidebar-item.active .sidebar-icon {
  color: #9ad12b;
}

.sidebar-label {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.login-sheet-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  flex-shrink: 0;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #9ad12b;
  background: rgba(154, 209, 43, 0.12);
  border: 1px solid rgba(154, 209, 43, 0.45);
  border-radius: 999px;
  padding: 0 6px;
  line-height: 1.5;
}

.sidebar-children {
  background-color: #111110;
}

.sidebar-child-item {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 5px 14px 5px 28px;
  cursor: pointer;
  color: #7a7a78;
  font-size: 0.82rem;
  transition: background 0.1s, color 0.1s;
  user-select: none;
  min-width: 0;
}
.sidebar-child-item > .sidebar-open {
  flex: 1;
  min-width: 0;
}
.sidebar-child-item > .sidebar-open span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}
.sidebar-child-item:hover {
  background-color: #1a1a17;
  color: #c0c0be;
}

.sidebar-child-icon {
  font-size: 0.65rem;
  color: #7a7a76;
  flex-shrink: 0;
}

.sidebar-child-item-reverse {
  font-style: italic;
  padding-left: 36px;
}
.sidebar-child-item-reverse > span:not(.sidebar-child-type) {
  opacity: 0.85;
}
.sidebar-child-icon-reverse {
  color: #6a7a3a;
}

.sidebar-child-type {
  margin-left: auto;
  flex-shrink: 0;
  white-space: nowrap;
  font-size: 0.7rem;
  color: #a8a8a4;
  font-family: monospace;
}

.import-column-list {
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #2e2e2b;
  border-radius: 4px;
  padding: 8px;
  background-color: #141412;
}
.import-column-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 0;
  margin: 0;
  cursor: pointer;
  color: #b0b0ae;
}
.import-column-checkbox {
  flex-shrink: 0;
  width: 1.15em;
  height: 1.15em;
  margin: 0;
  accent-color: #9ad12b;
  cursor: pointer;
}
.import-column-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.import-column-type {
  flex-shrink: 0;
  font-size: 0.75rem;
  color: #a8a8a4;
  font-family: monospace;
}

.drag-handle,
.column-drag-handle {
  cursor: grab;
  color: #7a7a76;
  font-size: 0.65rem;
  flex-shrink: 0;
  padding: 2px 2px 2px 0;
  transition: color 0.15s;
}
.drag-handle:hover,
.column-drag-handle:hover {
  color: #9ad12b;
}
.drag-handle:active,
.column-drag-handle:active {
  cursor: grabbing;
}

.drag-ghost {
  opacity: 0.4;
  background-color: #1a1a17;
  border-left: 2px solid #9ad12b;
}

.content-header {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 12px;
  padding: 14px 18px 10px;
  border-bottom: 1px solid #2e2e2b;
  flex-shrink: 0;
}

.content-heading,
.content-header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.content-header-actions {
  flex-wrap: wrap;
  margin-left: auto;
}

.delete-rows-btn {
  font-weight: 600;
  font-size: 0.8rem;
  padding: 4px 14px;
}

.history-toggle-btn {
  font-size: 0.8rem;
  padding: 4px 12px;
  color: #b0b0ae;
  border-color: #474842;
}
.history-toggle-btn:hover {
  background-color: #2a2a27;
  border-color: #a8a8a4;
  color: #e8e8e6;
}

.filter-toggle-btn {
  font-size: 0.8rem;
  padding: 4px 12px;
}
.filter-toggle-btn-active {
  color: #e8e8e6;
  border-color: #9ad12b;
}

.export-csv-btn {
  font-size: 0.8rem;
  padding: 4px 12px;
  color: #b0b0ae;
  border-color: #474842;
}
.export-csv-btn:hover:not(:disabled) {
  background-color: #2a2a27;
  border-color: #a8a8a4;
  color: #e8e8e6;
}

.new-row-btn {
  background-color: #9ad12b;
  border-color: #9ad12b;
  color: #1a1a17;
  font-weight: 600;
  font-size: 0.8rem;
  padding: 4px 14px;
}
.new-row-btn:hover {
  background-color: #b0e040;
  border-color: #b0e040;
  color: #1a1a17;
}
.new-row-rel-picker {
  width: 100%;
}
.new-row-rel-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
.bulk-actions-btn,
.scheduled-tasks-btn,
.workflow-hooks-btn {
  font-size: 0.8rem;
  padding: 4px 14px;
}
.bulk-actions-section-label {
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #888;
  margin-bottom: 8px;
}
.bulk-actions-match-info {
  margin-top: 8px;
  font-size: 0.85rem;
  color: #ccc;
}
.bulk-actions-match-rows {
  margin-top: 8px;
  max-height: 220px;
  overflow: auto;
  border: 1px solid #474842;
  border-radius: 4px;
}
.bulk-actions-match-more {
  margin-top: 6px;
  font-size: 0.8rem;
  color: #888;
}
.content-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #e8e8e6;
}
.content-unique-title {
  font-size: 0.8rem;
  color: #9ad12b;
  font-family: monospace;
}

/* Override Bootstrap's flex-shrink: 0 on .row > * so this column can shrink and enable scrolling */
.spreadsheet-content-col {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
  height: 100%;
  flex-shrink: 1 !important;
  background-color: #1e1e1b;
}

.spreadsheet-wrapper {
  padding: 0;
  flex: 1 1 0;
  min-height: 0;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-color: #3a3a37 #181916;
  scrollbar-width: thin;
}

/* Scrollbar styling for table area only */
.spreadsheet-wrapper::-webkit-scrollbar {
  width: 12px;
}

.spreadsheet-wrapper::-webkit-scrollbar-track {
  background: #181916;
}

.spreadsheet-wrapper::-webkit-scrollbar-thumb {
  background: #3a3a37;
  border-radius: 6px;
}

.spreadsheet-wrapper::-webkit-scrollbar-thumb:hover {
  background: #4a4a47;
}

.spreadsheet-table-container {
  min-width: 100px;
  margin: 0;
  padding: 0;
}

.job-progress-bar {
  padding: 8px 12px;
  background-color: #1a1a17;
  border-bottom: 1px solid #474842;
  flex-shrink: 0;
}

.job-progress-item {
  margin-bottom: 4px;
}

.job-progress-item:last-child {
  margin-bottom: 0;
}

.job-progress-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}

.job-progress-label {
  font-size: 12px;
  color: #b0b0ae;
}

.job-progress-pct {
  font-size: 12px;
  font-weight: 600;
  color: #4dabf7;
}

.job-progress-track {
  height: 6px;
  background-color: #2e2e2b;
  border-radius: 3px;
  overflow: hidden;
}

.job-progress-fill {
  height: 100%;
  background-color: #4dabf7;
  border-radius: 3px;
  transition: width 0.3s ease;
}

.context-nav-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  width: 100%;
}
button.sidebar-open {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  padding: 0;
  cursor: pointer;
}
button.sidebar-child-item {
  font: inherit;
  color: inherit;
  background: transparent;
  text-align: left;
  width: 100%;
}
.cell-display {
  cursor: pointer;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  padding: 4px 8px;
  display: block;
  width: 100%;
  border-radius: 3px;
  min-height: 1.4em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 300px;
}
.cell-display:hover {
  background-color: rgba(154, 209, 43, 0.1);
  outline: 1px solid rgba(154, 209, 43, 0.3);
}
.cell-display-readonly {
  cursor: default;
  color: #888;
}
.cell-display-readonly:hover {
  background-color: transparent;
  outline: none;
}
.cell-display-richtext {
  color: #b0b0ae;
  font-style: italic;
  font-size: 0.9em;
}

</style>

<style>
/* Global styles for spreadsheet table — dark theme overrides */

/* Set Bootstrap CSS vars so striped/hover calculations use our dark colors */
#hot-preview table,
.spreadsheet-table,
.spreadsheet-table.table {
  --bs-table-bg: #1e1e1b;
  --bs-table-color: #e8e8e6;
  --bs-table-border-color: #474842;
  --bs-table-striped-bg: #252520;
  --bs-table-striped-color: #e8e8e6;
  --bs-table-hover-bg: #2a2a27;
  --bs-table-hover-color: #e8e8e6;
  --bs-table-active-bg: #333330;
  background-color: #1e1e1b !important;
  color: #e8e8e6 !important;
  border-color: #474842 !important;
}

/* Borders on all cells — explicit border-width keeps th/td aligned under border-collapse */
#hot-preview th,
#hot-preview td,
.spreadsheet-table th,
.spreadsheet-table td {
  border: 1px solid #474842 !important;
  text-align: left;
}

/* Sticky header for virtual scroll */
.spreadsheet-sticky-header {
  position: sticky;
  top: 0;
  z-index: 2;
}

/* Header cells */
#hot-preview thead th,
.spreadsheet-table thead th {
  background-color: #242421 !important;
  color: #c8c8c6 !important;
  font-weight: 600;
  font-size: 0.82rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  padding: 10px 12px !important;
  border-color: #474842 !important;
  white-space: nowrap;
}

/* Body cells */
#hot-preview tbody td,
.spreadsheet-table tbody td {
  color: #e8e8e6 !important;
  background-color: #1e1e1b !important;
  font-size: 0.9rem;
  padding: 7px 12px !important;
  border-color: #474842 !important;
  vertical-align: middle;
}

/* Virtual scroll spacer rows */
.spreadsheet-table tr.virtual-spacer td {
  padding: 0 !important;
  border: none !important;
  background: none !important;
}

/* Striped rows — use class-based striping for virtual scroll compatibility */
#hot-preview tbody tr:nth-of-type(odd) td,
.spreadsheet-table tbody tr.table-row-odd td {
  background-color: #222220 !important;
}

/* Hover rows */
#hot-preview tbody tr:hover td,
.spreadsheet-table tbody tr:hover td {
  background-color: #2a2a27 !important;
}

.bulk-actions-match-table.spreadsheet-table thead th,
.bulk-actions-match-table.spreadsheet-table tbody td {
  padding: 4px 8px !important;
  font-size: 0.78rem;
}

/* Cell editor popover — dark theme */
.cell-editor-popover .popover-body {
  background-color: #2d2d2a;
  color: #e8e8e6;
}

.cell-editor-popover .popover-header {
  background-color: #1e1e1b;
  color: #e8e8e6;
  border-color: #474842;
}

.cell-editor-popover .form-control {
  background-color: #181915;
  color: #e8e8e6;
  border-color: #474842;
}

#hot-preview thead th.select-cell,
#hot-preview tbody td.select-cell,
.spreadsheet-table thead th.select-cell,
.spreadsheet-table tbody td.select-cell {
  width: 40px !important;
  min-width: 40px !important;
  max-width: 40px !important;
  text-align: center !important;
  vertical-align: middle !important;
  padding: 7px 4px !important;
  white-space: nowrap;
}

#hot-preview thead th.history-cell,
#hot-preview tbody td.history-cell,
.spreadsheet-table thead th.history-cell,
.spreadsheet-table tbody td.history-cell {
  width: 36px !important;
  min-width: 36px !important;
  max-width: 36px !important;
  text-align: center !important;
  vertical-align: middle !important;
  padding: 0 !important;
  white-space: nowrap;
}
.bulk-choice {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}
.bulk-choice-card {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  min-height: 160px;
  padding: 16px;
  background: #232320;
  border: 1px solid #474842;
  border-radius: 6px;
  color: #e8e8e6;
}
.bulk-choice-card:hover,
.bulk-choice-card:focus {
  border-color: #9ad12b;
  outline: none;
}
.bulk-choice-kicker {
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #9ad12b;
  margin-bottom: 6px;
}
.bulk-choice-title {
  font-size: 1.05rem;
  font-weight: 600;
  margin-bottom: 8px;
}
.bulk-choice-text {
  color: #b0b0ae;
  font-size: 0.85rem;
  line-height: 1.4;
}
.bulk-choice-meta {
  margin-top: auto;
  padding-top: 12px;
  font-size: 0.8rem;
  color: #e8e8e6;
}
.bulk-pane-back {
  background: none;
  border: 0;
  padding: 0 0 12px;
  color: #b0b0ae;
  font-size: 0.85rem;
}
.bulk-pane-back:hover,
.bulk-pane-back:focus {
  color: #e8e8e6;
}
.bulk-workflow-lead {
  margin: 0 0 10px;
  color: #b0b0ae;
  font-size: 0.85rem;
}
.bulk-workflow-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.bulk-workflow-card {
  display: block;
  width: 100%;
  text-align: left;
  padding: 12px 14px;
  background: #232320;
  border: 1px solid #474842;
  border-radius: 6px;
  color: #e8e8e6;
}
.bulk-workflow-card:hover,
.bulk-workflow-card:focus {
  border-color: #9ad12b;
  outline: none;
}
.bulk-workflow-card-selected {
  border-color: #9ad12b;
  box-shadow: inset 3px 0 0 #9ad12b;
}
.bulk-workflow-card-title {
  display: block;
  font-weight: 600;
}
.bulk-workflow-card-id {
  display: block;
  margin-top: 2px;
  color: #888;
  font-size: 0.75rem;
}
.bulk-workflow-card-desc {
  display: block;
  margin-top: 6px;
  color: #b0b0ae;
  font-size: 0.85rem;
  line-height: 1.35;
}
.bulk-actions-footer {
  display: flex;
  align-items: center;
  width: 100%;
  gap: 8px;
}
.bulk-actions-footer-spacer {
  flex: 1;
}
.bulk-workflow-message {
  margin: 0.4rem 0 0;
  font-size: 0.85rem;
  color: #9ad12b;
}
.bulk-workflow-message-error {
  color: #e07a7a;
}
@media (max-width: 640px) {
  .bulk-choice {
    grid-template-columns: 1fr;
  }
}

.row-select-checkbox {
  width: 15px;
  height: 15px;
  cursor: pointer;
  accent-color: #9ad12b;
}

.row-history-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  background: none;
  border: none;
  cursor: pointer;
  color: #6a6a67;
  padding: 0;
  margin: 0;
  vertical-align: middle;
  transition: color 0.15s;
  line-height: 1;
}
.row-history-btn:hover {
  color: #9ad12b;
}

.cell-image-thumb {
  display: block;
  max-height: 36px;
  max-width: 72px;
  object-fit: contain;
  border-radius: 3px;
}

.image-file-field input[type="file"] {
  color: #e8e8e6;
  max-width: 100%;
}

.cell-editor-popover .form-control:focus {
  background-color: #181915;
  color: #e8e8e6;
  border-color: #9ad12b;
  box-shadow: 0 0 0 0.2rem rgba(154, 209, 43, 0.25);
}

/* RuntimeFilterBuilder dark theme overrides */
.rfb-container {
  background-color: #1a1a17 !important;
  border-color: #2e2e2b !important;
  margin: 0 !important;
  border-radius: 0 !important;
  border-left: 0 !important;
  border-right: 0 !important;
  flex-shrink: 0;
}

.rfb-container .rfb-bar {
  padding: 6px 12px;
}

.rfb-container .rfb-label {
  color: #7a7a78 !important;
}

.rfb-container .rfb-form {
  background-color: #141412;
  border-color: #2e2e2b !important;
}

.rfb-container .form-select,
.rfb-container .form-control {
  background-color: #181915;
  color: #e8e8e6;
  border-color: #474842;
}

.rfb-container .form-select:focus,
.rfb-container .form-control:focus {
  background-color: #181915;
  color: #e8e8e6;
  border-color: #9ad12b;
  box-shadow: 0 0 0 0.2rem rgba(154, 209, 43, 0.25);
}

.rfb-container .rfb-rel-results {
  background-color: #181915;
  border-color: #474842 !important;
}

.rfb-container .rfb-rel-result-item:hover {
  background-color: #2a2a27 !important;
}

.choice-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
}
.choice-remove {
  cursor: pointer;
  color: #f82471;
}
.add-choice {
  cursor: pointer;
  color: #6cb6ff;
  margin-top: 8px;
}


</style>
